// src/types/api.ts

// Shared billing types from @levelup/central-billing
export type {
  SubscriptionStatus as CentralSubscriptionStatus,
  BillingProvider,
  CreditTransactionType,
  CreditWallet,
  CreditTransaction,
  CreditPurchaseStatus,
  BillingUnavailableReason,
} from '@levelup/central-billing';
export {
  SUBSCRIPTION_STATUSES,
  ACTIVE_SUBSCRIPTION_STATUSES,
  isSubscriptionStatus,
  normalizeSubscriptionStatus,
  isActiveSubscription,
  isBillingProvider,
  isCreditPurchaseStatus,
  isCreditPurchaseTerminal,
} from '@levelup/central-billing';
export type {
  CreditBundleCatalogItem,
  CreditBundlePurchase,
  CheckoutResponse,
  CheckoutErrorResponse,
  CreditPurchaseResponse,
  PortalSessionResponse,
  BillingOverviewBase,
} from '@levelup/central-billing';

/**
 * Central canonical plan codes.
 * These are the ONLY valid plan identifiers across all LevelUp apps.
 */
export type CentralPlanCode = 'free' | 'team' | 'enterprise';

/**
 * Purchasable plan codes (excludes free).
 */
export type PurchasablePlanCode = 'team' | 'enterprise';

/**
 * @deprecated Use CentralPlanCode instead. Alias for backward compatibility.
 */
export type WorkspacePlan = CentralPlanCode;
export type WorkspaceType = 'personal' | 'business';

/**
 * CRM subscription status — includes 'none' for workspaces without subscriptions.
 * Central's SubscriptionStatus (re-exported as CentralSubscriptionStatus) does not include 'none'.
 */
export type SubscriptionStatus = 'none' | 'trialing' | 'active' | 'past_due' | 'canceled';

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  type: WorkspaceType;
  plan: WorkspacePlan;
  industry: string | null;
  isDefault: boolean;
  isDemo: boolean;
  createdByUserId: string;
  trialEndsAt: string | null;
  subscriptionStatus: SubscriptionStatus;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserWorkspaceSummary {
  workspace: Workspace;
  role: string;
  isPrimary: boolean;
  isActive: boolean;
}

export type MenuKey =
  | 'home'
  | 'contacts'
  | 'companies'
  | 'tasks'
  | 'pipeline'
  | 'messages'
  | 'emails'
  | 'phone'
  | 'sms'
  | 'schedule'
  | 'circles'
  | 'referrals'
  | 'team'
  | 'forms'
  | 'files'
  | 'projects'
  | 'offerings'
  | 'payments'
  | 'businessProfile'
  | 'portfolio'
  | 'recruitment'
  | 'marketplace'
  | 'channels'
  | 'settings';

export interface MenuPermissions {
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

export interface ResolvedMenuItem {
  menuKey: MenuKey;
  label: string;
  order: number;
  isVisible: boolean;
  permissions: MenuPermissions;
}

export interface ResolvedMenuResponse {
  workspaceId: string;
  menu: ResolvedMenuItem[];
}

// Menu Template types
export type MenuTemplateWorkspaceType = 'personal' | 'business' | 'any';

export interface MenuTemplateItem {
  id: string;
  templateId: string;
  menuKey: MenuKey;
  label: string;
  order: number;
  isVisible: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MenuTemplate {
  id: string;
  name: string;
  workspaceType: MenuTemplateWorkspaceType;
  industry: string | null;
  targetRole: string | null;
  isDefault: boolean;
  items?: MenuTemplateItem[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateMenuTemplateRequest {
  name: string;
  workspaceType: MenuTemplateWorkspaceType;
  industry?: string | null;
  targetRole?: string | null;
  items?: Array<{
    menuKey: MenuKey;
    label: string;
    order?: number;
    isVisible?: boolean;
  }>;
}

export interface UpdateMenuTemplateRequest {
  name?: string;
  workspaceType?: MenuTemplateWorkspaceType;
  industry?: string | null;
  targetRole?: string | null;
}

export interface CreateMenuTemplateItemRequest {
  menuKey: MenuKey;
  label: string;
  order?: number;
  isVisible?: boolean;
}

export interface UpdateMenuTemplateItemRequest {
  menuKey?: MenuKey;
  label?: string;
  order?: number;
  isVisible?: boolean;
}

export type ConversationSource = 'levelup' | 'weopen' | 'other';
export type ConversationMode = 'chat' | 'voice' | 'mixed';
export type ConversationStatus = 'active' | 'pending_human' | 'human_active' | 'closed';
export type MessageSenderType = 'user' | 'assistant' | 'system' | 'external_user' | 'external_bot';
export type ActionStatus = 'pending' | 'running' | 'success' | 'failed' | 'cancelled';

export type ConversationActionType =
  | 'create_task'
  | 'update_task'
  | 'create_event'
  | 'update_event'
  | 'create_contact'
  | 'update_contact'
  | 'create_pipeline_opportunity'
  | 'update_pipeline_opportunity'
  | 'send_internal_message'
  | 'send_email'
  | 'send_sms'
  | 'add_memory'
  | 'note'
  | 'custom';

export type ConversationActionStatus =
  | 'pending'
  | 'pending_confirmation'
  | 'queued'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'skipped';

// Assistant V2: Who suggested the action
export type ConversationActionSuggestedBy = 'assistant' | 'user' | 'system';

export interface ConversationSession {
  id: string;
  workspaceId: string;
  initiatedByUserId: string | null;
  contactId: string | null;
  title: string | null;
  source: ConversationSource;
  mode: ConversationMode;
  status: ConversationStatus;
  externalSessionId: string | null;
  createdAt: string;
  updatedAt: string;
  contact?: ContactSummary | null;
}

export interface ConversationMessage {
  id: string;
  sessionId: string;
  senderType: MessageSenderType;
  senderUserId: string | null;
  contactId: string | null;
  content: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  contact?: ContactSummary | null;
}

export interface ConversationAction {
  id: string;
  sessionId?: string;
  conversationId?: string;
  workspaceId: string;
  triggeredByMessageId?: string | null;
  initiatedByUserId?: string | null;
  userId?: string | null;
  type?: string;
  actionType?: ConversationActionType;
  status?: ActionStatus | ConversationActionStatus;
  actionStatus?: ConversationActionStatus;
  targetType?: string | null;
  targetId?: string | null;
  summary?: string;
  payload?: Record<string, unknown> | null;
  result?: Record<string, unknown> | null;
  error?: Record<string, unknown> | null;
  errorMessage?: string | null;
  creditsSpent?: number | null;
  // Assistant V2 fields
  suggestedBy?: ConversationActionSuggestedBy;
  requiresConfirmation?: boolean;
  explanation?: string;
  confidence?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationDetailResponse {
  session: ConversationSession;
  messages: ConversationMessage[];
  actions: ConversationAction[];
}

// Request types
export interface CreateWorkspaceRequest {
  name: string;
  type: WorkspaceType;
  plan?: WorkspacePlan;
  industry?: string;
}

export interface CreateWorkspaceResponse {
  workspace: Workspace;
  role: string;
}

export interface SwitchWorkspaceResponse {
  success: boolean;
  activeWorkspaceId: string;
}

export interface CreateConversationRequest {
  workspaceId?: string;
  title?: string;
  mode?: ConversationMode;
}

export interface CreateConversationResponse {
  session: ConversationSession;
  messages: ConversationMessage[];
  actions: ConversationAction[];
}

export interface AddMessageRequest {
  content: string;
}

export interface AddMessageResponse {
  message: ConversationMessage;
  action: ConversationAction;
}

// Credit types — CreditWallet and CreditTransaction imported from @levelup/central-billing above

import type {
  CreditWallet,
  CreditTransaction,
  CreditBundleCatalogItem,
  CreditBundlePurchase,
  BillingUnavailableReason,
} from '@levelup/central-billing';

export interface WorkspaceCreditsResponse {
  workspaceId: string;
  wallets: CreditWallet[];
  recentTransactions: CreditTransaction[];
}

// Contact types
export type RelationshipType =
  | 'spouse' | 'partner' | 'child' | 'parent' | 'grandparent' | 'sibling'
  | 'stepchild' | 'stepparent' | 'ex_spouse' | 'co_parent'
  | 'friend' | 'close_friend'
  | 'nanny' | 'babysitter' | 'caregiver' | 'home_aide'
  | 'doctor' | 'therapist' | 'dentist' | 'specialist'
  | 'tutor' | 'coach' | 'teacher'
  | 'housekeeper' | 'handyman' | 'contractor'
  | 'emergency_contact' | 'neighbor' | 'other';

export interface Contact {
  id: string;
  workspaceId: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  companyId: string | null;
  company: string | null;
  title: string | null;
  type: string | null;
  tags: string[] | null;
  notes: string | null;
  createdByUserId: string;
  relationshipType: RelationshipType | null;
  dateOfBirth: string | null;
  // Hard suppression flag. Outbound must not dial or message a contact
  // carrying it. One-way: CRM can raise it but never clears it (see
  // `UpdateContactRequest.doNotContact`).
  doNotContact: boolean;
  createdAt: string;
  updatedAt: string;
  // How this contact entered the CRM (backend `Contact.source`, e.g. 'outbound'
  // | 'manual' | …) and, when synced from Outbound, the linked Outbound contact
  // id. Both are already returned by GET /contacts; surfaced here so the Phone
  // list can show which platform each contact came from. Read-only display —
  // NOT part of Contact Sync.
  source?: string | null;
  externalSourceId?: string | null;
  outboundContactId?: string | null;
  identifiers?: ContactIdentifier[];
}

// Contact Identifier types
export type ContactIdentifierType = 'email' | 'phone' | 'external_chat' | 'weopen' | 'twilio' | 'other';

export interface ContactIdentifier {
  id: string;
  workspaceId: string;
  contactId: string;
  type: ContactIdentifierType;
  value: string;
  verified: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Optional details for one Contact <-> Company link made by a create form.
 * `relationshipType` here is the per-company relationship (employee, advisor,
 * investor, ...) — NOT the Contact's own personal-workspace `relationshipType`.
 */
export interface ContactCompanyLinkDetails {
  roleTitle?: string | null;
  relationshipType?: string | null;
  /** `YYYY-MM-DD` */
  startDate?: string | null;
}

export interface CreateContactRequest {
  workspaceId?: string;
  fullName: string;
  email?: string;
  phone?: string;
  /**
   * CANONICAL company identity. The server translates it into a primary
   * `contact_companies` affiliation and then owns the display value — this is
   * Single-company form of `companyIds`, kept for existing callers.
   */
  companyId?: string;
  /**
   * Every Company to link the new contact to. The server validates each one
   * and links them in the same transaction that creates the contact — an id
   * it cannot link fails the create rather than being dropped.
   */
  companyIds?: string[];
  /** Which of `companyIds` is primary. Required when more than one is sent. */
  primaryCompanyId?: string | null;
  /** Optional role / relationship type / start date per selected company id. */
  companyLinkDetails?: Record<string, ContactCompanyLinkDetails>;
  /**
   * LEGACY free-text company name. Retained because the server still accepts
   * it and non-web API clients still send it; it is what produced contacts
   * carrying a company name with no Company record behind it. The server now
   * links it to an existing canonical Company when the name matches one, and
   * only falls back to storing the string when it does not. Prefer
   * `companyId` for every new write.
   */
  company?: string;
  title?: string;
  type?: string;
  tags?: string[];
  notes?: string;
  relationshipType?: RelationshipType;
  dateOfBirth?: string;
}

export interface UpdateContactRequest {
  fullName?: string;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  title?: string | null;
  type?: string | null;
  tags?: string[] | null;
  notes?: string | null;
  relationshipType?: RelationshipType | null;
  dateOfBirth?: string | null;
  // Do Not Contact is UPGRADE-ONLY, so the literal `true` is the only value
  // this request can carry — `false` is not expressible. The server ignores a
  // `false` regardless, but typing it this way stops an ordinary contact save
  // from ever implying a downgrade. Omit the field for every non-DNC edit.
  doNotContact?: true;
}

// Contact Context Types (GET /api/contacts/:id/context)

export type WorkspacePurpose = 'personal' | 'business' | 'client';

export interface ContactContextSignals {
  urgentTasks: any[];
  upcomingEvents: any[];
  staleInteraction: boolean;
}

export interface ContactContextNextAction {
  type: 'task' | 'event' | 'none';
  label: string;
  entityId?: string;
}

export interface ContactContextResponse {
  purpose: WorkspacePurpose;
  contact: {
    id: string;
    fullName: string;
    relationshipType: RelationshipType | null;
    dateOfBirth: string | null;
  };
  tasks: any[];
  events: any[];
  activity: any[];
  signals: ContactContextSignals;
  nextAction: ContactContextNextAction;
  contextSummary: string;
  contextBreakdown: Record<string, { tasks: number; events: number }>;
}

// Pagination type
export interface Pagination {
  total: number;
  limit: number;
  offset: number;
}

// Contacts response with pagination
export interface ContactsResponse {
  contacts: Contact[];
  pagination: Pagination;
  /** True if backend returned array (legacy mode) and we synthesized pagination */
  isFallbackArrayMode?: boolean;
}

// Company types
export interface Company {
  id: string;
  workspaceId: string;
  name: string;
  normalizedName: string;
  domain: string | null;
  contactCount: number;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CompaniesResponse {
  companies: Company[];
  pagination: Pagination;
}

export interface CreateCompanyRequest {
  name: string;
  domain?: string | null;
  /**
   * Existing Contacts to link in the same transaction as the create. Each
   * contact keeps its existing primary company; one with none gets this one.
   */
  contactIds?: string[];
  /** Optional role / relationship type / start date per selected contact id. */
  contactLinkDetails?: Record<string, ContactCompanyLinkDetails>;
}

export interface UpdateCompanyRequest {
  name?: string;
  domain?: string | null;
}

export interface ListCompaniesParams {
  q?: string;
  limit?: number;
  offset?: number;
}

// Contacts list filter params
export interface ListContactsParams {
  workspaceId?: string;
  companyId?: string;
  q?: string;
  type?: string; // CSV supported: "lead,client,vendor"
  tags?: string; // CSV supported: "vip,important"
  /** Members of this Circle (manual: active contact members; smart: its rules). */
  circleId?: string;
  limit?: number;
  offset?: number;
}

// Task types
export type TaskStatus = 'todo' | 'in_progress' | 'done' | 'blocked';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface TaskFollower {
  id: string;
  fullName: string | null;
  email: string;
}

export interface Task {
  id: string;
  workspaceId: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate: string | null;
  assigneeUserId: string | null;
  createdByUserId: string;
  relatedContactId: string | null;
  relatedContact?: { id: string; fullName: string } | null;
  tags: string[] | null;
  followers?: TaskFollower[];
  taskTemplateId?: string | null;
  checklistItems?: TaskChecklistItem[];
  /** Estimated effort in minutes for capacity planning */
  estimatedMinutes?: number | null;
  /** Timestamp when task was marked as done (used for on-time tracking) */
  completedAt?: string | null;
  /** Optional reason why the task is blocked */
  blockerReason?: string | null;
  /** Optional reason why the task was reopened after being done */
  reopenReason?: string | null;
  /** Optional manager-entered quality/completeness score (0–100) */
  completionScore?: number | null;
  /** When true, the task cannot be completed until a proof attachment exists */
  proofRequired?: boolean;
  /** Files attached to this task (populated by getTask). Includes proof attachments. */
  attachments?: FileAsset[];
  /** Optional link to the goal this task contributes to */
  goalId?: string | null;
  /** Optional link to the project this task belongs to */
  projectId?: string | null;
  /** Optional link to the milestone this task belongs to (project derived from it) */
  milestoneId?: string | null;
  /** Personal-workspace classification (medical, school, etc.). Nullable. */
  lifeContext?: LifeContext | null;
  /** External integration origin (e.g. 'patchproof'). Null = CRM-native. */
  source?: string | null;
  /** ID of this task in the external system. */
  externalSourceId?: string | null;
  /** Opaque context from the external system (severity, repo, CVE, URLs, etc.). */
  externalMeta?: Record<string, unknown> | null;
  /** Last time the external system synced this task. */
  externalLastSyncedAt?: string | null;
  /** Whether priority was set by system mapping or manually. */
  prioritySource?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Task Checklist Item
export interface TaskChecklistItem {
  id: string;
  taskId: string;
  title: string;
  completed: boolean;
  completedAt: string | null;
  completedByUserId: string | null;
  position: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateChecklistItemRequest {
  title: string;
  position?: number;
}

export interface UpdateChecklistItemRequest {
  title?: string;
  position?: number;
}

export interface ReorderChecklistItemsRequest {
  itemIds: string[];
}

export interface CreateTaskRequest {
  workspaceId?: string;
  title: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDate?: string;
  assigneeUserId?: string;
  relatedContactId?: string;
  tags?: string[];
  followerUserIds?: string[];
  estimatedMinutes?: number | null;
  blockerReason?: string | null;
  completionScore?: number | null;
  goalId?: string | null;
  projectId?: string | null;
  milestoneId?: string | null;
  proofRequired?: boolean;
}

export interface UpdateTaskRequest {
  title?: string;
  description?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDate?: string | null;
  assigneeUserId?: string | null;
  relatedContactId?: string | null;
  tags?: string[] | null;
  followerUserIds?: string[];
  estimatedMinutes?: number | null;
  blockerReason?: string | null;
  reopenReason?: string | null;
  completionScore?: number | null;
  goalId?: string | null;
  projectId?: string | null;
  milestoneId?: string | null;
  proofRequired?: boolean;
}

// Tasks response with pagination
export interface TasksResponse {
  tasks: Task[];
  pagination: Pagination;
  /** True if backend returned array (legacy mode) and we synthesized pagination */
  isFallbackArrayMode?: boolean;
}

export interface UpdateTaskStatusRequest {
  status: TaskStatus;
}

// Tasks list filter params
export interface ListTasksParams {
  workspaceId?: string;
  status?: TaskStatus;
  assigneeUserId?: string;
  relatedContactId?: string;
  projectId?: string;
  milestoneId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

// Task Template / Recurring Task types
export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface RecurrenceRule {
  frequency: RecurrenceFrequency;
  interval: number;
  daysOfWeek?: number[];
  dayOfMonth?: number;
  monthOfYear?: number;
  endDate?: string;
  count?: number;
}

export interface ChecklistItemTemplate {
  title: string;
  position: number;
}

// Management Summary

export interface ManagementCapacityEntry {
  userId: string;
  openTaskCount: number;
  estimatedMinutes: number;
  unestimatedTaskCount: number;
}

export interface ManagementGaps {
  overdueTasks: number;
  blockedTasks: number;
  overdueTaskIds: string[];
  blockedTaskIds: string[];
}

export interface ManagementProgress {
  totalTasksThisWeek: number;
  completedThisWeek: number;
  completionRate: number;
  weekStart: string;
  weekEnd: string;
}

export interface ManagementSummary {
  capacity: ManagementCapacityEntry[];
  gaps: ManagementGaps;
  progress: ManagementProgress;
}

export interface UserCapacityTask {
  id: string;
  title: string;
  status: string;
  priority: string;
  dueDate: string | null;
  estimatedMinutes: number | null;
  blockerReason: string | null;
  isOverdue: boolean;
}

export interface UserCapacityDetail {
  userId: string;
  openTaskCount: number;
  estimatedMinutes: number;
  unestimatedTaskCount: number;
  overdueTaskCount: number;
  blockedTaskCount: number;
  tasks: UserCapacityTask[];
}

export interface ReassignmentSuggestion {
  userId: string;
  openTasks: number;
  estimatedMinutes: number;
  unestimatedTasks: number;
  score: number;
  reason: string;
}

export interface ReassignmentResult {
  taskId: string;
  currentAssigneeUserId: string | null;
  taskHasNoEstimate: boolean;
  suggestions: ReassignmentSuggestion[];
}

export interface RebalancingUser {
  userId: string;
  openTasks: number;
  estimatedMinutes: number;
  unestimatedTasks: number;
}

export interface RebalancingRecommendation {
  taskId: string;
  taskTitle: string;
  fromUserId: string;
  toUserId: string;
  reason: string;
  score: number;
}

export interface TeamRebalancingResult {
  overloadedUsers: RebalancingUser[];
  underloadedUsers: RebalancingUser[];
  recommendations: RebalancingRecommendation[];
}

// ============================================================
// Goals
// ============================================================

export type GoalType = 'metric' | 'initiative';
export type GoalPeriodType = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually' | 'custom';
export type GoalStatus = 'active' | 'paused' | 'completed' | 'archived';
export type GoalRollupMode = 'manual' | 'derived';
export type GoalTrackingStatus = 'on_track' | 'behind' | 'ahead' | 'completed' | 'no_data';

export interface GoalProgress {
  goalId: string;
  progressPercent: number | null;
  trackingStatus: GoalTrackingStatus;
  periodElapsed: number | null;
  paceGap: number | null;
  source: 'metric' | 'task_completion' | 'manual_or_unavailable';
}

export interface GoalExecutionSummary {
  goalId: string;
  linkedTasks: number;
  completedTasks: number;
  overdueTasks: number;
  blockedTasks: number;
  completionRate: number | null;
  estimatedMinutesTotal: number;
  estimatedMinutesCompleted: number;
}

export interface Goal {
  id: string;
  workspaceId: string;
  title: string;
  description: string | null;
  linkedTaskGroupId?: string | null;
  goalType: GoalType;
  periodType: GoalPeriodType;
  status: GoalStatus;
  startDate: string;
  endDate: string;
  ownerUserId: string | null;
  createdByUserId: string;
  metricName: string | null;
  metricUnit: string | null;
  targetValue: string | null; // DECIMAL comes back as string from Sequelize
  currentValue: string | null;
  linkedProjectId: string | null;
  linkedMilestoneId: string | null;
  rollupMode: GoalRollupMode;
  notes: string | null;
  progress: GoalProgress;
  executionSummary?: GoalExecutionSummary;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGoalRequest {
  workspaceId?: string;
  title: string;
  goalType: GoalType;
  periodType: GoalPeriodType;
  startDate: string;
  endDate: string;
  description?: string | null;
  ownerUserId?: string | null;
  metricName?: string | null;
  metricUnit?: string | null;
  targetValue?: number | null;
  currentValue?: number | null;
  linkedProjectId?: string | null;
  linkedMilestoneId?: string | null;
  rollupMode?: GoalRollupMode;
  notes?: string | null;
}

export interface UpdateGoalRequest {
  title?: string;
  description?: string | null;
  periodType?: GoalPeriodType;
  status?: GoalStatus;
  startDate?: string;
  endDate?: string;
  ownerUserId?: string | null;
  metricName?: string | null;
  metricUnit?: string | null;
  targetValue?: number | null;
  currentValue?: number | null;
  linkedProjectId?: string | null;
  linkedMilestoneId?: string | null;
  rollupMode?: GoalRollupMode;
  notes?: string | null;
}

export interface GoalsResponse {
  goals: Goal[];
  total: number;
  limit: number;
  offset: number;
}

export interface TaskTemplate {
  id: string;
  workspaceId: string;
  title: string;
  description: string | null;
  priority: TaskPriority;
  assigneeUserIds: string[] | null;
  relatedContactId: string | null;
  goalId: string | null;
  tags: string[] | null;
  checklistTemplate: ChecklistItemTemplate[] | null;
  recurrenceRule: RecurrenceRule;
  startAt: string;
  timezone: string;
  isActive: boolean;
  lastGeneratedAt: string | null;
  nextOccurrenceAt: string | null;
  createdByUserId: string;
  creator?: { id: string; fullName: string; email: string };
  createdAt: string;
  updatedAt: string;
}

export interface CreateTaskTemplateRequest {
  workspaceId?: string;
  title: string;
  description?: string;
  priority?: TaskPriority;
  assigneeUserIds?: string[];
  relatedContactId?: string | null;
  goalId?: string | null;
  tags?: string[];
  checklistTemplate?: ChecklistItemTemplate[];
  recurrenceRule: RecurrenceRule;
  startAt: string;
  timezone?: string;
}

export interface UpdateTaskTemplateRequest {
  title?: string;
  description?: string | null;
  priority?: TaskPriority;
  assigneeUserIds?: string[] | null;
  relatedContactId?: string | null;
  goalId?: string | null;
  tags?: string[] | null;
  checklistTemplate?: ChecklistItemTemplate[] | null;
  recurrenceRule?: RecurrenceRule;
  startAt?: string;
  timezone?: string;
}

export interface ListTaskTemplatesParams {
  workspaceId?: string;
  isActive?: boolean;
}

// Pipeline types
export type OpportunityStatus = 'open' | 'won' | 'lost';

export interface PipelineStage {
  id: string;
  pipelineId: string;
  name: string;
  order: number;
  probability: number | null;
  isClosedWon: boolean;
  isClosedLost: boolean;
  /**
   * Deals in this stage THE CALLER CAN SEE (record-scoped on the server).
   * Present on `GET /pipelines/:id`; absent from the list endpoint.
   */
  dealCount?: number;
  createdAt: string;
  updatedAt: string;
}

/** What reaching a stage means for a deal. Maps to isClosedWon / isClosedLost. */
export type StageOutcome = 'open' | 'won' | 'lost';

export interface Pipeline {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  type: string | null;
  isDefault: boolean;
  createdByUserId: string;
  /**
   * Set when the pipeline is ARCHIVED (null/absent = active). An archived
   * pipeline keeps its deals as a read-only record: no new deals, no stage
   * moves, no structural edits until it is restored.
   */
  archivedAt?: string | null;
  archivedByUserId?: string | null;
  stages?: PipelineStage[];
  createdAt: string;
  updatedAt: string;
}

/** `GET /pipelines?status=` — active (the server default), archived, or all. */
export type PipelineListStatus = 'active' | 'archived' | 'all';

/** Response of `POST /pipelines/:id/archive` and `/restore`. */
export interface PipelineArchiveResponse {
  pipeline: Pipeline;
  /** False when it was already in that state. */
  changed: boolean;
  /** Names of automations that reference the pipeline or its stages (reported, never changed). */
  automationsReferencing: string[];
}

export interface Opportunity {
  id: string;
  workspaceId: string;
  pipelineId: string;
  stageId: string;
  title: string;
  amount: number | null;
  currency: string | null;
  status: OpportunityStatus;
  expectedCloseDate: string | null;
  contactId: string | null;
  description: string | null;
  createdByUserId: string;
  assignedToUserId: string | null;
  stage?: PipelineStage;
  contact?: { id: string; fullName: string } | null;
  assignee?: { id: string; email: string; fullName: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePipelineRequest {
  workspaceId?: string;
  name: string;
  description?: string;
  type?: string;
  isDefault?: boolean;
  /** Omitted → the server's default template. Created in the same transaction. */
  stages?: {
    name: string;
    order?: number;
    probability?: number | null;
    outcome?: StageOutcome;
    isClosedWon?: boolean;
    isClosedLost?: boolean;
  }[];
}

export interface UpdatePipelineRequest {
  name?: string;
  description?: string | null;
  type?: string | null;
  isDefault?: boolean;
}

export interface CreateStageRequest {
  name: string;
  /** Index to insert at. Omitted: an open stage goes before the won/lost stages. */
  position?: number;
  order?: number;
  probability?: number | null;
  outcome?: StageOutcome;
  isClosedWon?: boolean;
  isClosedLost?: boolean;
}

export interface UpdateStageRequest {
  name?: string;
  order?: number;
  probability?: number | null;
  outcome?: StageOutcome;
  isClosedWon?: boolean;
  isClosedLost?: boolean;
}

export interface CreateOpportunityRequest {
  workspaceId?: string;
  pipelineId: string;
  stageId: string;
  title: string;
  amount?: number;
  currency?: string;
  expectedCloseDate?: string;
  contactId?: string;
  description?: string;
  assignedToUserId?: string;
}

export interface UpdateOpportunityRequest {
  title?: string;
  amount?: number | null;
  currency?: string | null;
  expectedCloseDate?: string | null;
  contactId?: string | null;
  description?: string | null;
  assignedToUserId?: string | null;
}

export interface UpdateOpportunityStageRequest {
  stageId: string;
}

// Event types
export type LifeContext = 'medical' | 'school' | 'caregiving' | 'household' | 'errand' | 'travel' | 'other';

export type EventKind = 'meeting' | 'meetup' | 'webinar' | 'other';
export type EventCategory = 'virtual' | 'in_person' | 'hybrid';
export type EventAttendeeStatus = 'invited' | 'accepted' | 'declined' | 'tentative' | 'attended';

export interface EventAttendee {
  id: string;
  eventId: string;
  workspaceId: string;
  contactId: string | null;
  userId: string | null;
  externalEmail: string | null;
  role: string | null;
  status: EventAttendeeStatus;
  contact?: { id: string; fullName: string; email: string | null } | null;
  user?: { id: string; email: string; fullName: string } | null;
  createdAt: string;
  updatedAt: string;
}

// Provider sync status (explicit state tracking)
export type EventProviderSyncStatus = 'SYNCED' | 'PENDING' | 'UNLINKED' | 'ERROR' | 'DISABLED';

// Provider warning (non-blocking sync issues)
export interface EventProviderWarning {
  provider: 'zoom' | 'eventbrite';
  code: string;
  message: string;
  retryable?: boolean;
}

export interface Event {
  id: string;
  workspaceId: string;
  title: string;
  description: string | null;
  kind: EventKind;
  category: EventCategory;
  startAt: string;
  endAt: string | null;
  timezone: string | null;
  isAllDay: boolean;
  locationName: string | null;
  locationAddress: string | null;
  onlineUrl: string | null;
  zoomSyncEnabled: boolean;
  zoomMeetingId: string | null;
  zoomJoinUrl: string | null;
  zoomStartUrl: string | null;
  zoomSyncStatus: EventProviderSyncStatus;
  eventbriteSyncEnabled: boolean;
  eventbriteEventId: string | null;
  eventbriteUrl: string | null;
  eventbriteSyncStatus: EventProviderSyncStatus;
  createdByUserId: string;
  /** Personal-workspace classification (medical, school, etc.). Nullable. */
  lifeContext?: LifeContext | null;
  createdAt: string;
  updatedAt: string;
  attendees?: EventAttendee[];
  // Optional: returned when provider sync has warnings
  warnings?: EventProviderWarning[];
}

export interface CreateEventRequest {
  workspaceId?: string;
  title: string;
  description?: string;
  kind?: EventKind;
  category?: EventCategory;
  startAt: string;
  endAt?: string;
  timezone?: string;
  isAllDay?: boolean;
  locationName?: string;
  locationAddress?: string;
  onlineUrl?: string;
  zoomSyncEnabled?: boolean;
  eventbriteSyncEnabled?: boolean;
  lifeContext?: LifeContext;
  /** Phase D — canonical conversation/contact linkage when scheduled from a conversation. */
  contactId?: string;
  conversationThreadId?: string;
  attendees?: {
    contactId?: string;
    userId?: string;
    externalEmail?: string;
    role?: string;
    status?: EventAttendeeStatus;
  }[];
}

export interface UpdateEventRequest {
  title?: string;
  description?: string | null;
  kind?: EventKind;
  category?: EventCategory;
  startAt?: string;
  endAt?: string | null;
  timezone?: string | null;
  isAllDay?: boolean;
  locationName?: string | null;
  locationAddress?: string | null;
  onlineUrl?: string | null;
  zoomSyncEnabled?: boolean;
  eventbriteSyncEnabled?: boolean;
  lifeContext?: LifeContext | null;
  attendees?: {
    contactId?: string;
    userId?: string;
    externalEmail?: string;
    role?: string;
    status?: EventAttendeeStatus;
  }[];
}

// Circle types
export type CircleType =
  | 'people_group'
  | 'custom'
  | 'department'
  | 'team'
  | 'smart_segment'
  | 'territory'
  | 'account_team';

export const CIRCLE_TYPES: readonly CircleType[] = [
  'people_group',
  'custom',
  'department',
  'team',
  'smart_segment',
  'territory',
  'account_team',
];

export type CircleVisibility = 'private' | 'workspace' | 'restricted';

export const CIRCLE_VISIBILITIES: readonly CircleVisibility[] = [
  'private',
  'workspace',
  'restricted',
];

export type CircleMembershipStatus = 'active' | 'pending' | 'inactive';
export type CircleMembershipType = 'member' | 'manager' | 'observer';
export type CircleMemberAddedVia =
  | 'manual'
  | 'import'
  | 'api'
  | 'bulk'
  | 'system';

export interface CircleMember {
  id: string;
  circleId: string;
  workspaceId: string;
  contactId: string | null;
  userId: string | null;
  externalEmail: string | null;
  label: string | null;
  membershipStatus: CircleMembershipStatus;
  membershipType: CircleMembershipType;
  startDate: string | null;
  endDate: string | null;
  isPrimaryDepartment: boolean;
  addedByUserId: string | null;
  addedVia: CircleMemberAddedVia | null;
  notes: string | null;
  deletedAt: string | null;
  deletedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  contact?: { id: string; fullName: string; email: string | null } | null;
  user?: { id: string; email: string | null; fullName: string | null } | null;
}

export interface Circle {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  type: CircleType;
  visibility: CircleVisibility;
  createdByUserId: string;
  ownerUserId: string | null;
  parentCircleId: string | null;
  archivedAt: string | null;
  archivedByUserId: string | null;
  deletedAt: string | null;
  deletedByUserId: string | null;
  centralTeamId: string | null;
  createdAt: string;
  updatedAt: string;
  members?: CircleMember[];
  // Central-authoritative member count for department/team Circles.
  //   number  = active TeamMembership count from Central
  //   null    = Central said "unknown" (404/401/403/5xx/network/unavailable)
  //             OR the Circle is unlinked department/team (no source of truth)
  //   absent  = server did not resolve (mutation-response DTO shape)
  // For CRM-native Circle types (people_group/custom/smart_segment/
  // territory/account_team) this field is absent and callers should
  // fall back to `members?.length`.
  centralMemberCount?: number | null;
  // Phase 12 — Central Team Head-only Circle management. Set by the
  // getCircle detail endpoint; a UI HINT only. The backend re-verifies
  // Head status on every mutation independently — a client that hides
  // this flag cannot bypass the server's Head gate.
  //   true    = caller is active Head of `centralTeamId`; management
  //             UI (Edit, Archive, Delete, Link/Unlink) may show
  //   false   = caller is NOT Head; management UI must be hidden for
  //             this Central-backed department/team Circle
  //   null    = not applicable (CRM-native Circle, unlinked org
  //             Circle, or Head status could not be determined). The
  //             frontend falls back to the existing `circles.manage`
  //             capability signal for CRM-native paths.
  //   absent  = server did not resolve (list/mutation DTOs — call the
  //             detail endpoint for the authoritative answer)
  canManageAsHead?: boolean | null;
  // Smart Circles (type = 'smart_segment'). Membership is evaluated per
  // viewer from `smartRules` and never stored, so `members` is always [] for
  // a Smart Circle and the count travels separately.
  //   smartRules        canonical ContactFilter, or null (legacy row: no rules yet)
  //   smartRulesActive  false = "No rules yet — add rules to activate"
  //   smartMemberCount  matching contacts THE VIEWER may see (record-scoped)
  smartRules?: ContactFilter | null;
  smartRulesActive?: boolean;
  smartMemberCount?: number;
}

// ---- Canonical typed Contact filter (server/src/services/contactFilter) ----
export type ContactFilterField =
  | 'tags'
  | 'ownerUserId'
  | 'relationshipStatus'
  | 'type'
  | 'companyId'
  | 'hasEmail'
  | 'hasPhone'
  | 'lastContactedAt'
  | 'createdAt'
  | 'city'
  | 'state'
  | 'zip';

export interface ContactFilterRule {
  field: ContactFilterField;
  op: string;
  value?: unknown;
}

/** v1: every rule must match (AND). */
export interface ContactFilter {
  v: 1;
  all: ContactFilterRule[];
}

/** A field-level refusal from the server (400 INVALID_SMART_RULES). */
export interface ContactFilterError {
  /** e.g. `all[2].value` */
  path: string;
  code: string;
  message: string;
}

export interface SmartCirclePreview {
  /** Matching contacts the viewer may see. */
  count: number;
  /** Up to 5 of them, by name. */
  sample: Array<{ id: string; displayName: string }>;
}

export interface SmartCircleMember {
  id: string;
  displayName: string;
  email: string | null;
}

export interface CircleMemberInput {
  contactId?: string;
  userId?: string;
  externalEmail?: string;
  label?: string;
  membershipStatus?: CircleMembershipStatus;
  membershipType?: CircleMembershipType;
  startDate?: string | null;
  endDate?: string | null;
  isPrimaryDepartment?: boolean;
  notes?: string | null;
}

export interface CreateCircleRequest {
  workspaceId?: string;
  name: string;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  type?: CircleType;
  visibility?: CircleVisibility;
  ownerUserId?: string | null;
  parentCircleId?: string | null;
  centralTeamId?: string | null;
  members?: CircleMemberInput[];
  /** Required for, and only for, type 'smart_segment'. */
  smartRules?: ContactFilter;
}

export interface UpdateCircleRequest {
  name?: string;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  type?: CircleType;
  visibility?: CircleVisibility;
  ownerUserId?: string | null;
  parentCircleId?: string | null;
  centralTeamId?: string | null;
  /** Omit to keep the stored rules. */
  smartRules?: ContactFilter;
}

// Metadata-only summary returned by `GET /api/circles/central-teams`.
// Central owns the full Team resource; this is the minimal shape needed
// to drive the Circle → Central Team picker on the frontend.
export interface CentralTeamSummary {
  id: string;
  name: string;
  description: string | null;
  type: 'department' | 'team' | 'division' | 'region' | 'office' | 'custom';
  status: 'active' | 'archived';
}

export interface WorkspaceCentralTeamsResponse {
  /**
   * false when the Central Team SDK is not configured for this
   * environment. UI should surface the picker as disabled with a hint
   * rather than treating this as an error.
   */
  available: boolean;
  teams: CentralTeamSummary[];
}

// -----------------------------------------------------------------------------
// Circle People — source-discriminated response from GET /api/circles/:id/people
// -----------------------------------------------------------------------------
// Central-backed Circles (Department/Team + centralTeamId) display the Central
// Team roster in read-only mode. All other Circles fall back to the local
// CRM Contact / CircleMember rows and keep the existing add/remove UX.

export type CircleCentralError = 'not_found' | 'forbidden' | 'unavailable';

export interface CircleCentralMember {
  membershipId: string;
  userId: string;
  name: string | null;
  email: string | null;
  role: string; // TeamRole — SEMANTIC, never used for authorization
  isPrimary: boolean;
  status: 'active' | 'left';
}

export interface CircleContactMember {
  id: string;
  contactId: string | null;
  userId: string | null;
  externalEmail: string | null;
  label: string | null;
  membershipStatus: string;
  membershipType: string;
  isPrimaryDepartment: boolean;
  startDate: string | null;
  endDate: string | null;
  addedVia: string | null;
  notes: string | null;
  contact: { id: string; fullName: string | null; email: string | null } | null;
  user: { id: string; email: string | null; fullName: string | null } | null;
}

export type CirclePeopleResponse =
  | {
      source: 'central_team';
      readOnly: true;
      canManage: boolean;
      members: CircleCentralMember[];
      centralError?: CircleCentralError;
    }
  | {
      source: 'central_team_unlinked';
      readOnly: true;
      canManage: false;
      canLink: boolean;
    }
  | {
      source: 'crm_contacts';
      readOnly: false;
      canManage: boolean;
      members: CircleContactMember[];
    }
  | {
      // Smart Circle: the contacts matching its rules that THE VIEWER may
      // see, evaluated now. rulesActive=false → legacy row, no rules yet.
      source: 'smart_segment';
      readOnly: true;
      canManage: false;
      rulesActive: boolean;
      total: number;
      limit: number;
      offset: number;
      members: SmartCircleMember[];
    };

// Central Team roles — SEMANTIC labels only, never used for authorization.
export type CentralTeamRole =
  | 'Head'
  | 'Manager'
  | 'Supervisor'
  | 'Coordinator'
  | 'Member'
  | 'Observer';

export const CENTRAL_TEAM_ROLES: readonly CentralTeamRole[] = [
  'Head',
  'Manager',
  'Supervisor',
  'Coordinator',
  'Member',
  'Observer',
] as const;

export interface CircleCentralCandidate {
  userId: string;
  name: string | null;
  email: string | null;
  status: string;
  existingTeamMembership: boolean;
}

export interface CircleCentralCandidatesResponse {
  candidates: CircleCentralCandidate[];
}

export interface AddCircleCentralMemberRequest {
  userId: string;
  role?: CentralTeamRole;
  isPrimary?: boolean;
}

export interface UpdateCircleCentralMemberRequest {
  role?: CentralTeamRole;
  isPrimary?: boolean;
}

// =============================================================================
// Provider Assignment Domain (Person ↔ Provider ↔ Department ↔ Role)
// =============================================================================
// Backend contract mirror. Enum strings MUST match backend exactly —
// never redefine or reorder without a coordinated backend change.

export type AssignmentPriority =
  | 'primary'
  | 'secondary'
  | 'backup'
  | 'escalation'
  | 'consultant';

export const ASSIGNMENT_PRIORITIES: readonly AssignmentPriority[] = [
  'primary',
  'secondary',
  'backup',
  'escalation',
  'consultant',
] as const;

export type AssignmentStatus =
  | 'proposed'
  | 'active'
  | 'temporarily_inactive'
  | 'on_leave'
  | 'reassigned'
  | 'completed'
  | 'cancelled';

export const ASSIGNMENT_STATUSES: readonly AssignmentStatus[] = [
  'proposed',
  'active',
  'temporarily_inactive',
  'on_leave',
  'reassigned',
  'completed',
  'cancelled',
] as const;

export type AssignmentHistoryAction =
  | 'created'
  | 'provider_changed'
  | 'role_changed'
  | 'priority_changed'
  | 'status_changed'
  | 'dates_changed'
  | 'reassigned'
  | 'ended'
  | 'restored';

export interface ProviderRoleDefinition {
  id: string;
  workspaceId: string;
  key: string;
  name: string;
  description: string | null;
  canBePrimary: boolean;
  allowsMultiple: boolean;
  defaultResponsibilities: string[] | null;
  isSystem: boolean;
  isActive: boolean;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface PersonProviderAssignment {
  id: string;
  workspaceId: string;
  contactId: string;
  providerUserId: string;
  departmentCircleId: string;
  centralTeamId: string | null;
  providerRoleId: string;
  priority: AssignmentPriority;
  status: AssignmentStatus;
  startDate: string;
  endDate: string | null;
  responsibilities: string[] | null;
  assignmentReason: string | null;
  assignedByUserId: string | null;
  endedByUserId: string | null;
  endedReason: string | null;
  reassignedFromId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;

  // Included associations (present on GET list/detail via DEFAULT_INCLUDE).
  contact?: { id: string; fullName: string | null; email: string | null; phone: string | null } | null;
  provider?: { id: string; fullName: string | null; displayName: string | null; email: string } | null;
  departmentCircle?: { id: string; name: string; type: string; centralTeamId: string | null } | null;
  providerRole?: Pick<ProviderRoleDefinition, 'id' | 'key' | 'name' | 'canBePrimary' | 'allowsMultiple' | 'isActive'> | null;
  assignedBy?: { id: string; fullName: string | null; displayName: string | null; email: string } | null;
  endedBy?: { id: string; fullName: string | null; displayName: string | null; email: string } | null;
}

export interface PersonProviderAssignmentHistory {
  id: string;
  workspaceId: string;
  assignmentId: string;
  action: AssignmentHistoryAction;
  actorUserId: string | null;
  changeDiff: Record<string, unknown> | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  actor?: { id: string; fullName: string | null; displayName: string | null; email: string } | null;
}

export interface ListAssignmentsParams {
  workspaceId?: string;
  contactId?: string;
  providerUserId?: string;
  departmentCircleId?: string;
  providerRoleId?: string;
  priority?: AssignmentPriority;
  status?: AssignmentStatus;
  activeOnly?: boolean;
  limit?: number;
  offset?: number;
}

export interface CreateAssignmentRequest {
  workspaceId?: string;
  contactId: string;
  providerUserId: string;
  departmentCircleId: string;
  providerRoleId: string;
  priority?: AssignmentPriority;
  status?: AssignmentStatus;
  startDate?: string | null;
  endDate?: string | null;
  responsibilities?: string[] | null;
  assignmentReason?: string | null;
}

export interface UpdateAssignmentRequest {
  providerUserId?: string;
  departmentCircleId?: string;
  providerRoleId?: string;
  priority?: AssignmentPriority;
  status?: AssignmentStatus;
  startDate?: string | null;
  endDate?: string | null;
  responsibilities?: string[] | null;
  assignmentReason?: string | null;
}

export interface ReassignAssignmentRequest {
  newProviderUserId: string;
  newProviderRoleId?: string;
  newPriority?: AssignmentPriority;
  effectiveDate?: string | null;
  reason?: string | null;
  transferResponsibilities?: boolean;
  /**
   * Backend currently DEFERS task transfer — see spec §8 fallback.
   * The response carries `taskTransferDeferred: true` when this flag
   * is set. UI must not claim tasks were moved.
   */
  transferOpenTasks?: boolean;
}

export interface ReassignAssignmentResponse {
  previous: PersonProviderAssignment;
  next: PersonProviderAssignment;
  taskTransferDeferred: boolean;
}

export interface EndAssignmentRequest {
  endDate?: string | null;
  reason?: string | null;
  status?: 'completed' | 'cancelled';
}

export interface ProviderWorkload {
  providerUserId: string;
  totalActive: number;
  primary: number;
  secondary: number;
  backup: number;
  escalation: number;
  consultant: number;
}

export interface CreateProviderRoleRequest {
  workspaceId?: string;
  key: string;
  name: string;
  description?: string | null;
  canBePrimary?: boolean;
  allowsMultiple?: boolean;
  defaultResponsibilities?: string[] | null;
}

export interface UpdateProviderRoleRequest {
  name?: string;
  description?: string | null;
  canBePrimary?: boolean;
  allowsMultiple?: boolean;
  defaultResponsibilities?: string[] | null;
  isActive?: boolean;
}

// =============================================================================
// (existing) Circle helpers
// =============================================================================

export interface BulkAddMembersResponse {
  added: CircleMember[];
  skipped: Array<{ input: CircleMemberInput; reason: string }>;
}

export interface BulkRemoveMembersRequest {
  identifiers: Array<{
    contactId?: string;
    userId?: string;
    externalEmail?: string;
  }>;
}

export interface ListCirclesParams {
  workspaceId?: string;
  q?: string;
  type?: CircleType;
  includeArchived?: boolean;
  ownerUserId?: string;
  parentCircleId?: string | null;
  limit?: number;
  offset?: number;
}

// Referral types
export type ReferralDirection = 'given' | 'received';

export type ReferralReason =
  | 'showed_interest'
  | 'meets_expressed_needs'
  | 'may_share_synergy';

export type ReferralLikelihood = 'maybe' | 'likely' | 'very_likely';

export type ReferralStatus =
  | 'open'
  | 'in_progress'
  | 'closed_won'
  | 'closed_lost';

export interface Referral {
  id: string;
  workspaceId: string;
  direction: ReferralDirection;
  subjectContactId: string | null;
  recipientContactId: string | null;
  recipientCircleId: string | null;
  reason: ReferralReason;
  likelihood: ReferralLikelihood;
  status: ReferralStatus;
  notes: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  subjectContact?: { id: string; fullName: string; company: string | null; email: string | null } | null;
  recipientContact?: { id: string; fullName: string; company: string | null; email: string | null } | null;
  recipientCircle?: { id: string; name: string; color: string | null } | null;
  creator?: { id: string; email: string | null; fullName: string | null } | null;
}

export interface CreateReferralRequest {
  workspaceId?: string;
  direction: ReferralDirection;
  subjectContactId?: string;
  recipientContactId?: string;
  recipientCircleId?: string;
  reason: ReferralReason;
  likelihood: ReferralLikelihood;
  status?: ReferralStatus;
  notes?: string;
}

export interface UpdateReferralRequest {
  direction?: ReferralDirection;
  subjectContactId?: string | null;
  recipientContactId?: string | null;
  recipientCircleId?: string | null;
  reason?: ReferralReason;
  likelihood?: ReferralLikelihood;
  status?: ReferralStatus;
  notes?: string | null;
}

// Offering types
export type OfferingType = 'product' | 'service' | 'membership';
export type BillingModel = 'one_time' | 'recurring';
export type PriceInterval = 'day' | 'week' | 'month' | 'year';

export interface OfferingPrice {
  id: string;
  offeringId: string;
  name: string | null;
  billingModel: BillingModel;
  amount: number;
  currency: string;
  interval: PriceInterval | null;
  intervalCount: number | null;
  isDefault: boolean;
  stripePriceId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Offering {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  type: OfferingType;
  sku: string | null;
  isActive: boolean;
  defaultPriceId: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  prices?: OfferingPrice[];
  defaultPrice?: OfferingPrice | null;
}

export interface CreateOfferingRequest {
  workspaceId?: string;
  name: string;
  description?: string;
  type: OfferingType;
  sku?: string;
  isActive?: boolean;
  prices?: {
    name?: string;
    billingModel: BillingModel;
    amount: number;
    currency?: string;
    interval?: PriceInterval;
    intervalCount?: number;
    isDefault?: boolean;
  }[];
}

export interface UpdateOfferingRequest {
  name?: string;
  description?: string | null;
  type?: OfferingType;
  sku?: string | null;
  isActive?: boolean;
  defaultPriceId?: string | null;
}

export interface CreateOfferingPriceRequest {
  name?: string;
  billingModel: BillingModel;
  amount: number;
  currency?: string;
  interval?: PriceInterval;
  intervalCount?: number;
  isDefault?: boolean;
}

export interface UpdateOfferingPriceRequest {
  name?: string;
  isDefault?: boolean;
}

// Invoice/Payment types
export type InvoiceType = 'invoice' | 'estimate';
export type InvoiceStatus = 'draft' | 'sent' | 'viewed' | 'accepted' | 'partially_paid' | 'paid' | 'cancelled';
export type InvoiceScheduleStatus = 'pending' | 'paid' | 'overdue' | 'cancelled';
export type RecurringInterval = 'day' | 'week' | 'month' | 'year';

export interface InvoiceItem {
  id: string;
  invoiceId: string;
  offeringId: string | null;
  name: string;
  description: string | null;
  quantity: number;
  unitAmount: number;
  totalAmount: number;
  createdAt: string;
  updatedAt: string;
  offering?: Offering | null;
}

export interface InvoiceSchedule {
  id: string;
  invoiceId: string;
  label: string | null;
  dueDate: string | null;
  amount: number;
  status: InvoiceScheduleStatus;
  createdAt: string;
  updatedAt: string;
}

export interface Invoice {
  id: string;
  workspaceId: string;
  type: InvoiceType;
  status: InvoiceStatus;
  number: string | null;
  title: string | null;
  description: string | null;
  contactId: string | null;
  currency: string;
  subtotalAmount: number;
  taxAmount: number;
  totalAmount: number;
  amountPaid: number;
  dueDate: string | null;
  isRecurring: boolean;
  recurringInterval: RecurringInterval | null;
  recurringIntervalCount: number | null;
  stripeInvoiceId: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  items?: InvoiceItem[];
  schedules?: InvoiceSchedule[];
  contact?: { id: string; fullName: string; email: string | null } | null;
}

export interface CreateInvoiceRequest {
  workspaceId?: string;
  type: InvoiceType;
  title?: string;
  description?: string;
  contactId?: string;
  currency?: string;
  dueDate?: string;
  isRecurring?: boolean;
  recurringInterval?: RecurringInterval;
  recurringIntervalCount?: number;
  items?: {
    offeringId?: string;
    name: string;
    description?: string;
    quantity?: number;
    unitAmount: number;
  }[];
  schedules?: {
    label?: string;
    dueDate?: string;
    amount: number;
  }[];
}

export interface UpdateInvoiceRequest {
  title?: string | null;
  description?: string | null;
  contactId?: string | null;
  status?: InvoiceStatus;
  dueDate?: string | null;
  isRecurring?: boolean;
  recurringInterval?: RecurringInterval | null;
  recurringIntervalCount?: number | null;
  items?: {
    offeringId?: string;
    name: string;
    description?: string;
    quantity?: number;
    unitAmount: number;
  }[];
  schedules?: {
    label?: string;
    dueDate?: string;
    amount: number;
    status?: InvoiceScheduleStatus;
  }[];
}

export interface RecordPaymentRequest {
  amount: number;
  scheduleId?: string;
}

export interface AddInvoiceItemRequest {
  offeringId?: string;
  name: string;
  description?: string;
  quantity?: number;
  unitAmount: number;
}

export interface AddInvoiceScheduleRequest {
  label?: string;
  dueDate?: string;
  amount: number;
}

// Time Entry types
export type TimeEntryStatus = 'open' | 'closed' | 'cancelled';

export interface TimeEntry {
  id: string;
  workspaceId: string;
  userId: string;
  clockInAt: string;
  clockOutAt: string | null;
  breakMinutes: number;
  status: TimeEntryStatus;
  clockInLat: number | null;
  clockInLng: number | null;
  clockInLocationLabel: string | null;
  clockOutLat: number | null;
  clockOutLng: number | null;
  clockOutLocationLabel: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ClockInRequest {
  lat?: number;
  lng?: number;
  locationLabel?: string;
  notes?: string;
}

export interface ClockOutRequest {
  lat?: number;
  lng?: number;
  locationLabel?: string;
  breakMinutes?: number;
  notes?: string;
}

export interface UpdateTimeEntryRequest {
  clockInAt?: string;
  clockOutAt?: string | null;
  breakMinutes?: number;
  notes?: string | null;
  status?: TimeEntryStatus;
}

// Team types
export interface TeamMemberSummary {
  userId: string;
  workspaceId: string;
  fullName: string | null;
  email: string;
  avatarUrl: string | null;
  role: string | null;
  isOwner: boolean;
  isClockedIn: boolean;
  /**
   * Did Central actually answer?
   *
   * `isClockedIn: false` means two very different things depending on this
   * flag: Central said no, or Central said nothing. Rendering both as "not
   * working" states a fact about a person that nobody asserted.
   *
   * Optional because an older server predates the field; absent is treated as
   * `true` (Central answered), which is the pre-existing behaviour.
   */
  clockStateKnown?: boolean;
  currentEntryId?: string;
  clockInAt?: string;
}

/**
 * Selected assignment for invite-time visibility configuration.
 * Uses Central's canonical flattened format (one record per entry).
 */
export interface InviteSelectedAssignment {
  appId: string;
  entity: string;
  recordId: string;
}

export interface InviteTeamMemberRequest {
  email: string;
  /** Central role to assign. Sent as the authoritative role value. */
  role?: 'owner' | 'admin' | 'member' | 'viewer';
  /**
   * Central member-preset template ID. Sent alongside role='member'
   * to differentiate Agent / Coordinator / Analyst presets.
   */
  templateId?: string;
  /**
   * Optional selected assignments for invite-time visibility.
   * Uses Central's canonical flattened format: one entry per record.
   * Only sent when visibility template includes entities with level='selected'.
   */
  selectedAssignments?: InviteSelectedAssignment[];
}

export interface InviteTeamMemberResponse {
  message: string;
  email: string;
  role: string;
  workspaceId: string;
}

// File types
export type StorageProvider = 'local' | 's3' | 'gcs';

/**
 * C4.5 R3 — server-authoritative per-file affordances.
 *
 * canRead/canDownload follow the effective read authority (ordinary scope OR
 * an explicit FileAccessGrant). canEdit/canDelete/canShare follow ORDINARY
 * authority plus capability only, so a grant-only file is read/download-only
 * even for a principal with workspace-wide files.write or files.delete.
 *
 * Optional because older responses may omit it; consumers MUST treat absence
 * as "no mutation affordance" rather than falling back to global capability.
 */
export interface FileAccessFlags {
  canRead: boolean;
  canDownload: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canShare: boolean;
}

export interface FileAsset {
  access?: FileAccessFlags;
  id: string;
  workspaceId: string;
  uploaderUserId: string;
  originalName: string;
  mimeType: string | null;
  sizeBytes: number | null;
  storageProvider: StorageProvider;
  storageKey: string;
  publicUrl: string | null;
  description: string | null;
  contactId: string | null;
  projectId: string | null;
  taskId: string | null;
  isProof: boolean;
  createdAt: string;
  updatedAt: string;
  uploader?: { id: string; fullName: string; email: string } | null;
  contact?: { id: string; fullName: string } | null;
}

export interface FileShare {
  id: string;
  workspaceId: string;
  fileId: string;
  fromUserId: string;
  toUserId: string | null;
  toContactId: string | null;
  toCircleId: string | null;
  message: string | null;
  createdAt: string;
  updatedAt: string;
  file?: FileAsset | null;
  fromUser?: { id: string; fullName: string; email: string } | null;
}

export interface ReceivedFile {
  id: string;
  file: FileAsset;
  fromUser: { id: string; fullName: string; email: string };
  message: string | null;
  createdAt: string;
}

export interface CreateFileRequest {
  workspaceId?: string;
  originalName: string;
  mimeType?: string;
  sizeBytes?: number;
  storageProvider?: StorageProvider;
  storageKey: string;
  publicUrl?: string;
  description?: string;
  contactId?: string;
  projectId?: string;
  taskId?: string;
  isProof?: boolean;
}

export interface UpdateFileRequest {
  description?: string | null;
  contactId?: string | null;
  projectId?: string | null;
}

export interface ShareFileRequest {
  workspaceId?: string;
  toUserId?: string;
  toContactId?: string;
  toCircleId?: string;
  message?: string;
}

// Form types
export type FormType = 'traditional' | 'wizard';
export type FormFieldType =
  | 'text'
  | 'textarea'
  | 'email'
  | 'phone'
  | 'number'
  | 'select'
  | 'multi_select'
  | 'checkbox'
  | 'radio'
  | 'date'
  | 'file';

export interface FormField {
  id: string;
  formId: string;
  label: string;
  name: string;
  type: FormFieldType;
  options: string[] | null;
  isRequired: boolean;
  order: number;
  step: number | null;
  placeholder: string | null;
  helpText: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Form {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  type: FormType;
  isActive: boolean;
  successMessage: string | null;
  collectContact: boolean;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  creator?: { id: string; fullName: string; email: string } | null;
  fields?: FormField[];
}

export interface FormSubmission {
  id: string;
  workspaceId: string;
  formId: string;
  contactId: string | null;
  data: Record<string, unknown>;
  submittedAt: string;
  createdAt: string;
  updatedAt: string;
  form?: { id: string; name: string; type: FormType } | null;
  contact?: { id: string; fullName: string; email: string | null } | null;
}

export interface CreateFormRequest {
  workspaceId?: string;
  name: string;
  description?: string;
  type?: FormType;
  isActive?: boolean;
  successMessage?: string;
  collectContact?: boolean;
  fields?: {
    label: string;
    name: string;
    type?: FormFieldType;
    options?: string[];
    isRequired?: boolean;
    order?: number;
    step?: number;
    placeholder?: string;
    helpText?: string;
  }[];
}

export interface UpdateFormRequest {
  name?: string;
  description?: string | null;
  type?: FormType;
  isActive?: boolean;
  successMessage?: string | null;
  collectContact?: boolean;
  fields?: {
    label: string;
    name: string;
    type?: FormFieldType;
    options?: string[];
    isRequired?: boolean;
    order?: number;
    step?: number;
    placeholder?: string;
    helpText?: string;
  }[];
}

export interface CreateFormSubmissionRequest {
  data: Record<string, unknown>;
  contactId?: string;
}

// Project types
export type ProjectStatus = 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';
export type MilestoneStatus = 'pending' | 'in_progress' | 'completed';
export type DeliverableStatus = 'pending' | 'in_progress' | 'completed';

// Progress / execution summaries (computed on-read by the backend, like Goals)
export interface MilestoneExecutionSummary {
  milestoneId: string;
  linkedTasks: number;
  completedTasks: number;
  overdueTasks: number;
  blockedTasks: number;
  totalDeliverables: number;
  completedDeliverables: number;
  completionRate: number | null;
  remainingWorkItems: number;
}

export interface MilestoneProgress {
  milestoneId: string;
  progressPercent: number | null;
  trackingStatus: GoalTrackingStatus;
  remainingWorkItems: number;
}

export interface ProjectExecutionSummary {
  projectId: string;
  totalMilestones: number;
  completedMilestones: number;
  linkedTasks: number;
  completedTasks: number;
  overdueTasks: number;
  blockedTasks: number;
  completionRate: number | null;
  remainingWorkItems: number;
}

export interface ProjectProgress {
  projectId: string;
  progressPercent: number | null;
  trackingStatus: GoalTrackingStatus;
  remainingWorkItems: number;
}

export interface ProjectMilestone {
  id: string;
  projectId: string;
  name: string;
  description: string | null;
  dueDate: string | null;
  status: MilestoneStatus;
  order: number;
  createdAt: string;
  updatedAt: string;
  deliverables?: ProjectDeliverable[];
  progress?: MilestoneProgress;
  executionSummary?: MilestoneExecutionSummary;
}

export interface ProjectDeliverable {
  id: string;
  projectId: string;
  milestoneId: string | null;
  name: string;
  description: string | null;
  dueDate: string | null;
  status: DeliverableStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectContact {
  id: string;
  projectId: string;
  contactId: string;
  role: string | null;
  createdAt: string;
  updatedAt: string;
  contact?: { id: string; fullName: string; email: string | null; phone: string | null; company: string | null } | null;
}

export interface Project {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  status: ProjectStatus;
  pocContactId: string | null;
  startDate: string | null;
  endDate: string | null;
  budgetAmount: number | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: { id: string; fullName: string; email: string } | null;
  pocContact?: { id: string; fullName: string; email: string | null; phone: string | null; company: string | null } | null;
  milestones?: ProjectMilestone[];
  deliverables?: ProjectDeliverable[];
  projectContacts?: ProjectContact[];
  files?: FileAsset[];
  progress?: ProjectProgress;
  executionSummary?: ProjectExecutionSummary;
}

export interface CreateProjectRequest {
  workspaceId?: string;
  name: string;
  description?: string;
  status?: ProjectStatus;
  pocContactId?: string;
  startDate?: string;
  endDate?: string;
  budgetAmount?: number;
  milestones?: {
    name: string;
    description?: string;
    dueDate?: string;
    status?: MilestoneStatus;
    order?: number;
  }[];
  contacts?: {
    contactId: string;
    role?: string;
  }[];
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string | null;
  status?: ProjectStatus;
  pocContactId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  budgetAmount?: number | null;
}

export interface CreateMilestoneRequest {
  name: string;
  description?: string;
  dueDate?: string;
  status?: MilestoneStatus;
  order?: number;
}

export interface UpdateMilestoneRequest {
  name?: string;
  description?: string | null;
  dueDate?: string | null;
  status?: MilestoneStatus;
  order?: number;
}

export interface CreateDeliverableRequest {
  name: string;
  description?: string;
  dueDate?: string;
  status?: DeliverableStatus;
  milestoneId?: string;
}

export interface UpdateDeliverableRequest {
  name?: string;
  description?: string | null;
  dueDate?: string | null;
  status?: DeliverableStatus;
  milestoneId?: string | null;
}

export interface AddProjectContactRequest {
  contactId: string;
  role?: string;
}

export interface UpdateProjectContactRequest {
  role?: string | null;
}

// BusinessProfile types
export interface SocialLinks {
  facebook?: string | null;
  twitter?: string | null;
  linkedin?: string | null;
  instagram?: string | null;
  youtube?: string | null;
  tiktok?: string | null;
}

export interface BusinessProfile {
  id: string;
  workspaceId: string;
  // Internal
  internalName: string | null;
  internalNotes: string | null;
  internalTags: string[] | null;
  internalConfig: Record<string, unknown> | null;
  // Market
  displayName: string | null;
  tagline: string | null;
  description: string | null;
  logoUrl: string | null;
  coverImageUrl: string | null;
  websiteUrl: string | null;
  phone: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  socialLinks: SocialLinks | null;
  // Visibility
  isPublic: boolean;
  publicSlug: string | null;
  publicTheme: Record<string, unknown> | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  creator?: { id: string; fullName: string; email: string } | null;
}

export interface UpdateBusinessProfileRequest {
  // Internal
  internalName?: string | null;
  internalNotes?: string | null;
  internalTags?: string[] | null;
  internalConfig?: Record<string, unknown> | null;
  // Market
  displayName?: string | null;
  tagline?: string | null;
  description?: string | null;
  logoUrl?: string | null;
  coverImageUrl?: string | null;
  websiteUrl?: string | null;
  phone?: string | null;
  email?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  socialLinks?: SocialLinks | null;
  // Visibility
  isPublic?: boolean;
  publicSlug?: string | null;
  publicTheme?: Record<string, unknown> | null;
}

export interface PublicBusinessProfile {
  id: string;
  displayName: string | null;
  tagline: string | null;
  description: string | null;
  logoUrl: string | null;
  coverImageUrl: string | null;
  websiteUrl: string | null;
  phone: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  socialLinks: SocialLinks | null;
  publicTheme: Record<string, unknown> | null;
}

// Portfolio types
export type PortfolioType = 'general' | 'transformations' | 'gallery' | 'mixed';
export type PortfolioItemType = 'image' | 'video' | 'before_after' | 'mixed';

export interface PortfolioItem {
  id: string;
  portfolioId: string;
  title: string;
  description: string | null;
  itemType: PortfolioItemType;
  category: string | null;
  tags: string[] | null;
  primaryImageUrl: string | null;
  secondaryImageUrl: string | null;
  videoUrl: string | null;
  contactId: string | null;
  projectId: string | null;
  order: number;
  isFeatured: boolean;
  createdAt: string;
  updatedAt: string;
  contact?: { id: string; fullName: string; email: string | null } | null;
  project?: { id: string; name: string } | null;
}

export interface Portfolio {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  coverImageUrl: string | null;
  type: PortfolioType;
  isPublic: boolean;
  publicSlug: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  creator?: { id: string; fullName: string; email: string } | null;
  items?: PortfolioItem[];
}

export interface PortfolioCategory {
  id: string;
  workspaceId: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePortfolioRequest {
  workspaceId?: string;
  name: string;
  description?: string;
  coverImageUrl?: string;
  type?: PortfolioType;
  isPublic?: boolean;
  publicSlug?: string;
}

export interface UpdatePortfolioRequest {
  name?: string;
  description?: string | null;
  coverImageUrl?: string | null;
  type?: PortfolioType;
  isPublic?: boolean;
  publicSlug?: string | null;
}

export interface CreatePortfolioItemRequest {
  title: string;
  description?: string;
  itemType?: PortfolioItemType;
  category?: string;
  tags?: string[];
  primaryImageUrl?: string;
  secondaryImageUrl?: string;
  videoUrl?: string;
  contactId?: string;
  projectId?: string;
  order?: number;
  isFeatured?: boolean;
}

export interface UpdatePortfolioItemRequest {
  title?: string;
  description?: string | null;
  itemType?: PortfolioItemType;
  category?: string | null;
  tags?: string[] | null;
  primaryImageUrl?: string | null;
  secondaryImageUrl?: string | null;
  videoUrl?: string | null;
  contactId?: string | null;
  projectId?: string | null;
  order?: number;
  isFeatured?: boolean;
}

export interface ReorderPortfolioItemsRequest {
  itemIds: string[];
}

export interface CreatePortfolioCategoryRequest {
  workspaceId?: string;
  name: string;
}

export interface PublicPortfolio {
  id: string;
  name: string;
  description: string | null;
  coverImageUrl: string | null;
  type: PortfolioType;
  items?: {
    id: string;
    title: string;
    description: string | null;
    itemType: PortfolioItemType;
    category: string | null;
    tags: string[] | null;
    primaryImageUrl: string | null;
    secondaryImageUrl: string | null;
    videoUrl: string | null;
    order: number;
    isFeatured: boolean;
  }[];
}

// ============================================================
// Unibox / Emails Types
// ============================================================

export type MessageMedium = 'email' | 'sms' | 'in_app' | 'whatsapp' | 'call_log' | 'chat' | 'call' | 'voicemail' | 'other';
export type MessageDirection = 'inbound' | 'outbound';
/**
 * Mirrors `server/src/models/CommunicationMessage.ts`. See there for why
 * `accepted` is not `sent` and `outcome_unknown` is not `failed`.
 */
export type MessageStatus =
  | 'queued'
  | 'sending'
  | 'accepted'
  | 'sent'
  | 'delivered'
  | 'read'
  | 'failed'
  | 'outcome_unknown';

/** 'channel' = legacy per-medium thread; 'unified' = per-contact mixed-medium thread */
export type ThreadType = 'channel' | 'unified';

export interface CommunicationThread {
  id: string;
  workspaceId: string;
  primaryContactId: string | null;
  subject: string | null;
  channelSummary: string | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  lastMessageMedium: MessageMedium | null;
  isArchived: boolean;
  createdByUserId: string;
  // Live chat fields
  source: 'email' | 'sms' | 'weopen' | 'twilio_voice' | 'internal' | 'other';
  externalId: string | null;
  isLive: boolean;
  // Unified messaging fields
  primaryMedium: 'email' | 'sms' | 'chat' | 'call' | 'voicemail' | null;
  externalAddress: string | null;
  /**
   * E0a — the canonical marker of a platform-owned (system) channel thread.
   * Non-null means replies must go through the certified system-channel reply
   * route, never the ordinary send path. Serialized by the LIST endpoint
   * (`getThreads`) and thread detail alike, since it is a real model column.
   * This is the ONLY signal used to detect a system-channel thread — never
   * subject/sender/medium/title/participant/timestamp.
   */
  systemChannelId?: string | null;
  unreadCount: number;
  /** Thread type: 'channel' (legacy per-medium) or 'unified' (per-contact, any medium) */
  threadType: ThreadType;
  createdAt: string;
  updatedAt: string;
  primaryContact?: ContactSummary | null;
  creator?: UserSummary;
  messages?: CommunicationMessage[];
  /** Augmented by POST /api/unibox/threads/unified — contact details for channel availability */
  contact?: { id: string; fullName: string | null; email: string | null; phone: string | null } | null;
}

export interface CommunicationMessage {
  id: string;
  threadId: string;
  workspaceId: string;
  medium: MessageMedium;
  direction: MessageDirection;
  status: MessageStatus;
  fromAddress: string | null;
  toAddress: string | null;
  ccAddresses: string[] | null;
  bccAddresses: string[] | null;
  subject: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  attachments: { name: string; url: string; size?: number; type?: string }[] | null;
  externalMessageId: string | null;
  inReplyToMessageId: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  isFavorite: boolean;
  contactId: string | null;
  senderUserId: string | null;
  rawMetadata: Record<string, unknown> | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  thread?: CommunicationThread;
  contact?: ContactSummary | null;
  sender?: UserSummary | null;
  reactions?: { emoji: string; count: number; userIds: string[] }[];
}

export interface SavedMessageFolder {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  creator?: UserSummary;
  messageCount?: number;
  savedMessages?: SavedMessage[];
}

export interface SavedMessage {
  id: string;
  folderId: string;
  messageId: string;
  workspaceId: string;
  note: string | null;
  savedByUserId: string;
  createdAt: string;
  updatedAt: string;
  folder?: SavedMessageFolder;
  message?: CommunicationMessage;
  savedBy?: UserSummary;
}

// Thread Requests
export interface ListThreadsParams {
  workspaceId?: string;
  q?: string;
  medium?: MessageMedium;
  primaryMedium?: 'sms' | 'email' | 'all';
  contactType?: ContactType;
  contactId?: string;
  unreadOnly?: boolean;
  isArchived?: boolean;
  limit?: number;
  offset?: number;
}

export interface ThreadsResponse {
  threads: CommunicationThread[];
  pagination: Pagination;
  /** True if backend returned array (legacy mode) and we synthesized pagination */
  isFallbackArrayMode?: boolean;
}

export interface CreateThreadRequest {
  workspaceId?: string;
  primaryContactId?: string;
  subject?: string;
  /** Recipient address (email or phone) — used to resolve contact and reuse existing threads */
  toAddress?: string;
  /** Medium hint for the new thread (e.g. 'email', 'sms') */
  medium?: string;
}

export interface UpdateThreadRequest {
  subject?: string;
  isArchived?: boolean;
}

// Message Requests
export interface ListMessagesParams {
  workspaceId?: string;
  threadId?: string;
  medium?: MessageMedium;
  direction?: MessageDirection;
  isFavorite?: boolean;
  limit?: number;
  offset?: number;
}

export interface MessagesResponse {
  messages: CommunicationMessage[];
  pagination: Pagination;
  /** True if backend returned array (legacy mode) and we synthesized pagination */
  isFallbackArrayMode?: boolean;
}

// Email Requests
export interface SendEmailRequest {
  workspaceId?: string;
  threadId?: string;
  contactId?: string;
  toAddress: string;
  ccAddresses?: string[];
  bccAddresses?: string[];
  subject?: string;
  bodyText?: string;
  bodyHtml?: string;
  attachments?: { name: string; url: string; size?: number; type?: string }[];
}

export interface SendEmailResponse {
  message: CommunicationMessage;
  providerStatus: string;
  providerNote: string;
}

// Folder Requests
export interface CreateFolderRequest {
  workspaceId?: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
}

export interface UpdateFolderRequest {
  name?: string;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
}

export interface SaveMessageToFolderRequest {
  messageId: string;
  note?: string;
}

export interface MessageFolderInfo {
  savedMessageId: string;
  folder: {
    id: string;
    name: string;
    color: string | null;
    icon: string | null;
  };
  note: string | null;
  savedAt: string;
}

// ============================================================
// Channels
// ============================================================

export type ChannelType = 'request' | 'chatbot' | 'stream' | 'video';
export type ChannelPaymentType = 'free' | 'credits_per_use' | 'subscription' | 'one_time';
export type ChannelTrialType = 'none' | 'duration' | 'credits';
export type ChannelSubscriptionStatus = 'active' | 'trialing' | 'cancelled' | 'paused';
export type ChannelExecutionType = 'request' | 'chat' | 'stream_update' | 'video_view' | 'other';
export type ChannelExecutionStatus = 'success' | 'failed' | 'pending';
export type ChannelContentItemType = 'video' | 'post' | 'article' | 'link' | 'other';

export interface ChannelConfig {
  prompt?: string;
  model?: string;
  maxTokens?: number;
  temperature?: number;
  webhookUrl?: string;
  [key: string]: unknown;
}

export interface Channel {
  id: string;
  workspaceId: string;
  name: string;
  slug: string;
  description: string | null;
  type: ChannelType;
  iconUrl: string | null;
  paymentType: ChannelPaymentType;
  creditCost: number;
  subscriptionPriceId: string | null;
  oneTimePriceId: string | null;
  trialType: ChannelTrialType;
  trialDays: number | null;
  trialCredits: number | null;
  isActive: boolean;
  showInMarket: boolean;
  config: ChannelConfig | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  subscriptions?: ChannelSubscription[];
  contentItems?: ChannelContentItem[];
}

export interface ChannelSubscription {
  id: string;
  workspaceId: string;
  channelId: string;
  subscriberWorkspaceId: string | null;
  subscriberUserId: string | null;
  status: ChannelSubscriptionStatus;
  startedAt: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  totalCreditsUsed: number;
  lastUsedAt: string | null;
  meta: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  channel?: Channel;
  subscriberUser?: { id: string; fullName: string; email: string };
}

export interface ChannelExecution {
  id: string;
  workspaceId: string;
  channelId: string;
  subscriptionId: string | null;
  userId: string | null;
  contactId: string | null;
  type: ChannelExecutionType;
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  creditCost: number;
  status: ChannelExecutionStatus;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  channel?: Channel;
  user?: { id: string; fullName: string; email: string };
  contact?: { id: string; fullName: string; email: string };
  subscription?: ChannelSubscription;
}

export interface ChannelContentItem {
  id: string;
  workspaceId: string;
  channelId: string;
  title: string;
  description: string | null;
  type: ChannelContentItemType;
  mediaUrl: string | null;
  thumbnailUrl: string | null;
  publishedAt: string | null;
  isPublished: boolean;
  sortOrder: number;
  meta: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChannelExecutionStats {
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  pendingExecutions: number;
  totalCreditsUsed: number;
  executionsLast24Hours: number;
  executionsLast7Days: number;
  successRate: string | number;
}

// Channel Requests
export interface CreateChannelRequest {
  workspaceId?: string;
  name: string;
  slug: string;
  description?: string;
  type: ChannelType;
  iconUrl?: string;
  paymentType?: ChannelPaymentType;
  // creditCost (-> Channel.creditCostPerUse) is platform-set; the server
  // refuses a changed value from workspace users (403 CHANNEL_PRICE_NOT_EDITABLE).
  subscriptionPriceId?: string;
  oneTimePriceId?: string;
  trialType?: ChannelTrialType;
  trialDays?: number;
  trialCredits?: number;
  isActive?: boolean;
  showInMarket?: boolean;
  config?: ChannelConfig;
}

export interface UpdateChannelRequest {
  name?: string;
  slug?: string;
  description?: string | null;
  type?: ChannelType;
  iconUrl?: string | null;
  paymentType?: ChannelPaymentType;
  // creditCost (-> Channel.creditCostPerUse) is platform-set; the server
  // refuses a changed value from workspace users (403 CHANNEL_PRICE_NOT_EDITABLE).
  subscriptionPriceId?: string | null;
  oneTimePriceId?: string | null;
  trialType?: ChannelTrialType;
  trialDays?: number | null;
  trialCredits?: number | null;
  isActive?: boolean;
  showInMarket?: boolean;
  config?: ChannelConfig | null;
}

export interface TriggerExecutionRequest {
  channelId: string;
  subscriptionId?: string;
  contactId?: string;
  type?: ChannelExecutionType;
  input?: Record<string, unknown>;
}

export interface ChannelExecutionListResponse {
  executions: ChannelExecution[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateChannelContentRequest {
  title: string;
  description?: string;
  type?: ChannelContentItemType;
  mediaUrl?: string;
  thumbnailUrl?: string;
  publishedAt?: string;
  isPublished?: boolean;
  sortOrder?: number;
  meta?: Record<string, unknown>;
}

export interface UpdateChannelContentRequest {
  title?: string;
  description?: string | null;
  type?: ChannelContentItemType;
  mediaUrl?: string | null;
  thumbnailUrl?: string | null;
  publishedAt?: string | null;
  isPublished?: boolean;
  sortOrder?: number;
  meta?: Record<string, unknown> | null;
}

export interface SubscribeToChannelRequest {
  subscriberWorkspaceId?: string;
}

// ============================================================
// Recruitment Types
// ============================================================

export type RecruitmentOpportunityStatus = 'draft' | 'open' | 'paused' | 'closed';
export type RecruitmentOpportunityVisibility = 'internal' | 'workspace_only' | 'marketplace';
export type MarketplacePublishStatus = 'unpublished' | 'published' | 'sync_error';
export type RecruitmentApplicationStatus =
  | 'pending'
  | 'under_review'
  | 'shortlisted'
  | 'accepted'
  | 'rejected'
  | 'withdrawn';

export interface RecruitmentOpportunity {
  id: string;
  workspaceId: string;
  createdByUserId: string;
  title: string;
  description: string | null;
  location: string | null;
  employmentType: string | null;
  category: string | null;
  tags: string[] | null;
  status: RecruitmentOpportunityStatus;
  visibility: RecruitmentOpportunityVisibility;
  showInMarketplace: boolean;
  marketplaceStatus: MarketplacePublishStatus;
  marketplaceListingId: string | null;
  marketplacePublicUrl: string | null;
  publishedAt: string | null;
  lastSyncedAt: string | null;
  syncError: string | null;
  maxCandidates: number | null;
  compensationRange: string | null;
  currency: string | null;
  appliedCount: number;
  createdAt: string;
  updatedAt: string;
  workspace?: { id: string; name: string; slug: string | null };
  creator?: { id: string; displayName: string; email: string };
  applications?: RecruitmentApplication[];
}

export interface RecruitmentApplication {
  id: string;
  opportunityId: string;
  applicantWorkspaceId: string | null;
  applicantUserId: string | null;
  applicantContactId: string | null;
  status: RecruitmentApplicationStatus;
  message: string | null;
  source: string | null;
  createdAt: string;
  updatedAt: string;
  opportunity?: RecruitmentOpportunity;
  applicantWorkspace?: { id: string; name: string; slug: string | null };
  applicantUser?: { id: string; displayName: string; email: string };
  applicantContact?: { id: string; displayName: string; email: string | null };
}

export interface CreateRecruitmentOpportunityRequest {
  workspaceId?: string;
  title: string;
  description?: string;
  location?: string;
  employmentType?: string;
  category?: string;
  tags?: string[];
  status?: RecruitmentOpportunityStatus;
  visibility?: RecruitmentOpportunityVisibility;
  showInMarketplace?: boolean;
  maxCandidates?: number;
  compensationRange?: string;
  currency?: string;
}

export interface UpdateRecruitmentOpportunityRequest {
  title?: string;
  description?: string | null;
  location?: string | null;
  employmentType?: string | null;
  category?: string | null;
  tags?: string[] | null;
  status?: RecruitmentOpportunityStatus;
  visibility?: RecruitmentOpportunityVisibility;
  showInMarketplace?: boolean;
  maxCandidates?: number | null;
  compensationRange?: string | null;
  currency?: string | null;
}

export interface CreateRecruitmentApplicationRequest {
  opportunityId: string;
  applicantWorkspaceId?: string;
  applicantContactId?: string;
  message?: string;
  source?: string;
}

export interface UpdateRecruitmentApplicationRequest {
  status?: RecruitmentApplicationStatus;
  message?: string | null;
}

// ============================================================
// Talent Profile Types
// ============================================================

export type TalentAvailability =
  | 'full_time'
  | 'part_time'
  | 'contract'
  | 'freelance'
  | 'not_available';

export interface TalentProfileLinks {
  portfolio?: string;
  linkedin?: string;
  github?: string;
  twitter?: string;
  website?: string;
  [key: string]: string | undefined;
}

export interface TalentProfile {
  id: string;
  userId: string;
  workspaceId: string | null;
  headline: string | null;
  bio: string | null;
  skills: string[];
  location: string | null;
  availability: TalentAvailability;
  desiredRoles: string[];
  links: TalentProfileLinks | null;
  showInMarketplace: boolean;
  marketplaceStatus: MarketplacePublishStatus;
  marketplaceListingId: string | null;
  marketplacePublicUrl: string | null;
  publishedAt: string | null;
  lastSyncedAt: string | null;
  syncError: string | null;
  createdAt: string;
  updatedAt: string;
  user?: { id: string; displayName: string; email: string; avatarUrl?: string | null };
  workspace?: { id: string; name: string; slug: string | null };
}

export interface UpsertTalentProfileRequest {
  workspaceId?: string;
  headline?: string;
  bio?: string;
  skills?: string[];
  location?: string;
  availability?: TalentAvailability;
  desiredRoles?: string[];
  links?: TalentProfileLinks;
  showInMarketplace?: boolean;
}

// ============================================================
// Marketplace Types
// ============================================================

export type MarketplaceItemType = 'talent' | 'opportunity' | 'offering' | 'channel';

export interface MarketplaceItem {
  type: MarketplaceItemType;
  id: string;
  title: string;
  description: string | null;
  workspace: {
    id: string;
    name: string;
    slug: string | null;
  } | null;
  meta: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/**
 * A channel as another workspace sees it in the marketplace
 * (`GET /channels/marketplace`, `/marketplace/channels`, `/marketplace/featured`).
 * The server sends ONLY these fields; the publisher's `config` (prompt, vector
 * store, API URL) and creator never leave the owning workspace.
 */
export interface MarketplaceChannel {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  type: ChannelType;
  category: string | null;
  tags: string[] | null;
  paymentType: ChannelPaymentType;
  creditCostPerUse: number | null;
  subscriptionMonthlyCredits: number | null;
  trialType: ChannelTrialType;
  trialDurationDays: number | null;
  trialCredits: number | null;
  createdAt: string;
  updatedAt: string;
  workspace: { id: string; name: string; slug: string | null } | null;
}

export interface MarketplaceFeatured {
  talent: TalentProfile[];
  opportunities: RecruitmentOpportunity[];
  offerings: Offering[];
  channels: MarketplaceChannel[];
}

// Helper types
export interface UserSummary {
  id: string;
  fullName: string | null;
  email: string;
  avatarUrl?: string | null;
}

export interface ContactSummary {
  id: string;
  fullName: string;
  email: string | null;
  phone?: string | null;
}

export type ContactType = 'client' | 'lead' | 'partner' | 'vendor' | 'other';

// ============================================================
// Integration Types
// ============================================================

export type IntegrationAuthType = 'api_key' | 'oauth2' | 'webhook' | 'none';
export type IntegrationAccountMode = 'tenant_provided' | 'roi_internal' | 'subaccount';
export type IntegrationProvider =
  // `stripe` remains in this union because it is a value that EXISTS in
  // `integration_connections.appKey` on databases that predate the Payments
  // boundary. It is deliberately absent from `INTEGRATION_CATALOG`, so no card
  // offers it and no new one can be created; the type still has to describe
  // rows that are already stored.
  | 'stripe'
  // The canonical payment surface. Processor selection, processor credentials,
  // execution, webhooks and refunds all belong to LevelUp Payments; CRM only
  // ever reports status.
  | 'payments'
  | 'twilio'
  | 'eventbrite'
  | 'zoom'
  | 'ghl'
  | 'gmail'
  | 'outlook'
  | 'google_calendar'
  | 'weopen'
  | 'custom';
export type IntegrationStatus = 'connected' | 'disconnected' | 'error' | 'pending';

export interface IntegrationCredentials {
  apiKey?: string;
  accessToken?: string;
  refreshToken?: string;
  [key: string]: unknown;
}

export interface IntegrationSettings {
  siteId?: string;
  webhookSecret?: string;
  [key: string]: unknown;
}

export interface IntegrationConnection {
  id: string;
  workspaceId: string;
  userId: string | null;
  appKey: string;
  authType: IntegrationAuthType;
  credentials: IntegrationCredentials | null;
  settings: IntegrationSettings | null;
  displayName: string | null;
  status: IntegrationStatus;
  accountMode: IntegrationAccountMode;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Zoom only (B2E). Granular attendance-sync capability derived from the
   * persisted OAuth grant — independent of the connection `status`, which stays
   * `connected` (meeting CRUD works) even when this is `authorization_required`.
   *   available              — the grant includes past-participants access.
   *   authorization_required — connected, but attendance needs re-authorization.
   *   unavailable            — not in a connected state (status covers it).
   */
  zoomAttendanceCapability?: 'available' | 'authorization_required' | 'unavailable';
  /**
   * Zoom only (B3E). Granular cloud-recording capability derived from the
   * persisted OAuth grant — independent of `status` and of attendance. A missing
   * recording grant NEVER disconnects the integration; it only asks for one more
   * authorization.
   *   available              — the grant includes cloud_recording read access.
   *   authorization_required — connected, but recording needs re-authorization.
   *   unavailable            — not in a connected state (status covers it).
   */
  zoomRecordingCapability?: 'available' | 'authorization_required' | 'unavailable';
  /**
   * Zoom only. Aggregate meeting-management capability derived from the
   * persisted OAuth grant: 'available' ONLY when the grant holds all four
   * meeting:*:meeting scopes (read+write+update+delete). A create-only grant
   * that cannot update/delete reads 'authorization_required'. Independent of
   * attendance/recording; a missing meeting grant never disconnects the
   * integration.
   *   available              — the grant fully manages meetings (all four scopes).
   *   authorization_required — connected, but meeting CRUD needs re-authorization.
   *   unavailable            — not in a connected state (status covers it).
   */
  zoomMeetingCapability?: 'available' | 'authorization_required' | 'unavailable';
}

export interface CreateIntegrationRequest {
  appKey: string;
  authType: IntegrationAuthType;
  displayName?: string;
  credentials?: IntegrationCredentials;
  settings?: IntegrationSettings;
}

export interface UpdateIntegrationRequest {
  displayName?: string;
  credentials?: IntegrationCredentials;
  settings?: IntegrationSettings;
  status?: IntegrationStatus;
}

export interface TestIntegrationResponse {
  success: boolean;
  message: string;
  integration: IntegrationConnection;
}

// ============================================================
// Integration Wizard Types
// ============================================================

export type IntegrationWizardStatus = 'not_connected' | 'connected' | 'error' | 'needs_attention';

export type WizardProvider = 'twilio' | 'stripe' | 'gmail' | 'outlook' | 'zoom' | 'ghl' | 'eventbrite' | 'retell';

export const WIZARD_PROVIDERS: WizardProvider[] = ['twilio', 'stripe', 'gmail', 'outlook', 'zoom', 'ghl', 'eventbrite', 'retell'];

export interface IntegrationProviderStatus {
  provider: WizardProvider;
  status: IntegrationWizardStatus;
  lastCheckedAt?: string;
  message?: string;
  integrationId?: string;
  /**
   * Who owns this integration row.
   *   'crm'      — native CRM connection with credentials stored locally.
   *   'outbound' — Outbound Provider-Sync mirror; credentials are null
   *                on the CRM side by design.
   * Absent when no row exists (`status === 'not_connected'`).
   */
  managedBy?: 'crm' | 'outbound';
}

export interface GetIntegrationStatusResponse {
  statuses: IntegrationProviderStatus[];
}

export interface TestIntegrationByProviderRequest {
  provider: WizardProvider;
  config?: Record<string, unknown>;
}

export interface TestIntegrationByProviderResponse {
  success: boolean;
  message: string;
}

export interface SetupIntegrationRequest {
  provider: WizardProvider;
  config: Record<string, unknown>;
  settings?: Record<string, unknown>;
  displayName?: string;
}

export interface SetupIntegrationResponse {
  integration: {
    id: string;
    provider: string;
    status: IntegrationStatus;
    displayName: string | null;
    lastSyncedAt: string | null;
  };
  testResult: {
    success: boolean;
    message: string;
  };
}

// ============================================================
// Telephony / Twilio Types
// ============================================================

export type TwilioNumberStatus = 'active' | 'inactive' | 'pending' | 'failed';
export type TwilioAssignedType = 'none' | 'circle' | 'team_member';

export interface TwilioPhoneNumberCapabilities {
  voice: boolean;
  sms: boolean;
  mms: boolean;
}

export interface TwilioPhoneNumber {
  id: string;
  workspaceId: string;
  phoneNumber: string;
  friendlyName: string | null;
  label: string | null;
  twilioSid: string;
  status: TwilioNumberStatus;
  assignedType: TwilioAssignedType;
  assignedToId: string | null;
  isPrimary: boolean;
  capabilities: TwilioPhoneNumberCapabilities;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  // Enriched fields from API
  assignedUserName?: string;
  assignedCircleName?: string;
}

export interface RegisterPhoneNumberRequest {
  phoneNumber: string;
  twilioSid: string;
  friendlyName?: string;
  capabilities?: TwilioPhoneNumberCapabilities;
}

export interface UpdatePhoneNumberRequest {
  friendlyName?: string;
  status?: TwilioNumberStatus;
}

export interface AssignPhoneNumberRequest {
  assignedType: TwilioAssignedType;
  assignedToId?: string;
}

export interface SyncPhoneNumbersResponse {
  success: boolean;
  message?: string;
  /** Total numbers processed by this sync run */
  synced: number;
  /** Numbers newly inserted by this sync run */
  added?: number;
  /** Numbers that already existed and were updated in place */
  updated?: number;
}

// ============================================================
// Call Session Types
// ============================================================

export type CallDirection = 'inbound' | 'outbound';
export type CallStatus =
  | 'initiated'
  | 'ringing'
  | 'in-progress'
  | 'completed'
  | 'busy'
  | 'no-answer'
  | 'canceled'
  | 'failed';

export interface CallSession {
  id: string;
  workspaceId: string;
  twilioPhoneNumberId: string | null;
  assignedUserId: string | null;
  contactId: string | null;
  // Nullable: placeholder rows for Outbound-managed outgoing calls exist
  // between the prepare response and the recording webhook stamp.
  twilioCallSid: string | null;
  direction: CallDirection;
  status: CallStatus;
  fromNumber: string;
  toNumber: string;
  startedAt: string | null;
  answeredAt: string | null;
  endedAt: string | null;
  duration: number | null;
  recordingUrl: string | null;
  recordingSid: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  // Populated relations
  twilioPhoneNumber?: TwilioPhoneNumber;
  assignedUser?: { id: string; firstName: string | null; lastName: string | null; email: string };
  contact?: { id: string; firstName: string | null; lastName: string | null; email: string | null; phone: string | null };
  voicemail?: Voicemail;
}

// ============================================================
// Voicemail Types
// ============================================================

export type VoicemailStatus = 'new' | 'listened' | 'archived';
export type TranscriptionStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface Voicemail {
  id: string;
  workspaceId: string;
  twilioPhoneNumberId: string | null;
  assignedUserId: string | null;
  contactId: string | null;
  callSessionId: string | null;
  twilioRecordingSid: string;
  fromNumber: string;
  toNumber: string;
  recordingUrl: string;
  duration: number;
  transcription: string | null;
  transcriptionStatus: TranscriptionStatus;
  status: VoicemailStatus;
  listenedAt: string | null;
  listenedByUserId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  // Populated relations
  twilioPhoneNumber?: TwilioPhoneNumber;
  assignedUser?: { id: string; firstName: string | null; lastName: string | null; email: string };
  contact?: { id: string; firstName: string | null; lastName: string | null; email: string | null; phone: string | null };
  callSession?: CallSession;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface CallSessionFilters {
  phoneNumberId?: string;
  direction?: CallDirection;
  status?: CallStatus;
  ownership?: 'all' | 'mine' | 'unassigned';
  /** ADDITIVE (Recordings tab) — narrows to rows that DO or DO NOT have a recording. */
  hasRecording?: boolean;
  /** ADDITIVE — free-text across fromNumber/toNumber/CallSid/RecordingSid/contact/agent. */
  q?: string;
  /** ADDITIVE — half-open date-range on createdAt. Sent as ISO strings. */
  dateFrom?: string;
  dateTo?: string;
  /** ADDITIVE — sort. */
  sortBy?: 'createdAt' | 'duration';
  sortDir?: 'ASC' | 'DESC';
  limit?: number;
  offset?: number;
}

export interface VoicemailFilters {
  phoneNumberId?: string;
  status?: VoicemailStatus;
  ownership?: 'all' | 'mine' | 'unassigned';
  limit?: number;
  offset?: number;
}

// ============================================================
// Billing Types
// ============================================================

/**
 * @deprecated Use CentralPlanCode instead. Alias for backward compatibility.
 */
export type WorkspacePlanKey = CentralPlanCode;
export type WorkspaceSubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'incomplete';

export interface WorkspaceSubscription {
  id: string;
  workspaceId: string;
  planKey: WorkspacePlanKey;
  status: WorkspaceSubscriptionStatus;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  meta: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

// BillingUnavailableReason imported from @levelup/central-billing above
// CreditBundleCatalogItem imported from @levelup/central-billing above

/**
 * CRM plan catalog item — extends shared PlanCatalogItem with CRM-specific fields.
 * Uses `key` (CRM field name) mapped from Central's `planCode`.
 */
export interface PlanCatalogItem {
  key: CentralPlanCode;
  name: string;
  description: string;
  monthlyPriceCents: number;
  includedCreditsPerMonth: number;
  trialDays: number;
  trialAvailable: boolean;
  isCurrent: boolean;
}

export interface BillingOverview {
  workspaceId: string;
  workspaceType: WorkspaceType;
  planKey: WorkspacePlanKey;
  planName: string;
  planDescription: string;
  planStatus: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  allowAI: boolean;
  creditBalance: number;
  billingBlocked: boolean;
  cancelAtPeriodEnd: boolean;
  /** Whether billing/payment collection is available */
  billingAvailable: boolean;
  /** The billing provider being used (central, stripe, none) */
  billingProvider: 'central' | 'stripe' | 'none';
  /** Reason billing is unavailable (if applicable) */
  billingUnavailableReason?: BillingUnavailableReason;
  /**
   * Plan catalog for dynamic UI rendering.
   * Server is the single source of truth - frontend has no hard-coded plans.
   */
  planCatalog: PlanCatalogItem[];
  /**
   * Credit bundle catalog for dynamic UI rendering.
   * Server is the single source of truth - frontend has no hard-coded bundles.
   */
  creditBundleCatalog: CreditBundleCatalogItem[];
}

// CreditBundlePurchase imported from @levelup/central-billing above

export interface WorkspacePlanConfig {
  key: WorkspacePlanKey;
  name: string;
  description: string;
  monthlyPriceCents: number;
  includedCreditsPerMonth: number;
  allowAI: boolean;
}

export interface CreditBundleConfig {
  key: string;
  name: string;
  credits: number;
  priceCents: number;
}

// Billing Requests
export interface StartSubscriptionRequest {
  /**
   * Central canonical plan code for checkout.
   * Valid values: 'team' or 'enterprise'
   */
  planCode: PurchasablePlanCode;
  /**
   * @deprecated Use planCode instead. Kept for backward compatibility.
   */
  planKey?: 'standard' | 'ai';
}

/**
 * Response from POST /api/billing/workspaces/:id/start-subscription
 * Returns a Stripe checkout URL for redirect.
 */
export interface StartSubscriptionResponse {
  ok: true;
  checkoutUrl: string;
  sessionId: string;
}

/**
 * Error response from subscription checkout.
 */
export interface StartSubscriptionError {
  ok: false;
  error: string;
  errorCode?: string;
}

/**
 * @deprecated Use StartSubscriptionResponse instead.
 * Old response shape that expected subscription object directly.
 */
export interface LegacyStartSubscriptionResponse {
  subscription: WorkspaceSubscription;
  wallet: {
    balance: number;
  };
}

export interface UpdateSubscriptionRequest {
  planKey?: 'standard' | 'ai';
  cancelAtPeriodEnd?: boolean;
}

export interface PurchaseCreditBundleRequest {
  bundleKey: string;
}

export interface PurchaseCreditBundleResponse {
  success: boolean;
  purchase: CreditBundlePurchase;
  wallet: {
    balance: number;
  };
}

// ============================================================
// Assistant Request/Response Types
// ============================================================

// Note: ConversationActionType, ConversationActionStatus, ConversationAction,
// and ConversationMessage types are defined earlier in this file

export interface AssistantExecuteRequest {
  conversationId?: string;
  messageId?: string;
  message?: string;
  autoExecute?: boolean;
}

export interface AssistantExecuteResponse {
  conversationId: string;
  message: ConversationMessage;
  actions: ConversationAction[];
}

export interface UpdateConversationActionRequest {
  status?: ActionStatus;
  actionStatus?: ConversationActionStatus;
}

// ============================================================
// Assistant V2 Request/Response Types
// ============================================================

export interface AssistantPlanOptions {
  autoQueueHighConfidence?: boolean;
  autoQueueThreshold?: number;
  maxActions?: number;
  includeMemory?: boolean;
  historyMessageCount?: number;
}

export interface AssistantPlanRequest {
  conversationId?: string;
  message: string;
  options?: AssistantPlanOptions;
}

export interface AssistantPlanResponse {
  conversationId: string;
  message: ConversationMessage;
  actions: ConversationAction[];
  reasoning?: string;
  noActionReason?: string;
}

export interface AssistantConfirmRequest {
  actionIds?: string[];
  conversationId?: string;
  confirmAll?: boolean;
  executeImmediately?: boolean;
}

export interface AssistantConfirmResponse {
  confirmed: number;
  actions: ConversationAction[];
}

export interface AssistantSkipRequest {
  actionIds?: string[];
  conversationId?: string;
  skipAll?: boolean;
}

export interface AssistantSkipResponse {
  skipped: number;
  actions: ConversationAction[];
}

// ============================================================
// Live Chat / WeOpen Session Types
// ============================================================

export type WeOpenSessionStatus = 'open' | 'assigned' | 'closed';

export interface LiveSessionDto {
  id: string;
  externalSessionId: string;
  status: WeOpenSessionStatus;
  assignedUserId: string | null;
  assignedUserName: string | null;
  lastVisitorMessageAt: string | null;
  lastAgentMessageAt: string | null;
  threadId: string;
  conversationId: string | null;
  latestMessagePreview: string | null;
  subject: string | null;
  hasUnread: boolean;
  createdAt: string;
}

export interface LiveSessionsResponse {
  sessions: LiveSessionDto[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface LiveSessionMessagesResponse {
  messages: CommunicationMessage[];
  session: LiveSessionDto;
}

export interface AssignLiveSessionRequest {
  userId?: string;
}

export interface SendLiveChatMessageRequest {
  content: string;
}

export interface SendLiveChatMessageResponse {
  message: CommunicationMessage;
  conversationMessage?: ConversationMessage;
}

// ============================================================
// Onboarding Types
// ============================================================

export type OnboardingStep = 'welcome' | 'profile' | 'business' | 'finish';
// H-2C2 Phase 2 cleanup follow-up — `OnboardingRole` removed alongside
// dropping `roles` from /api/onboarding/presets. The wizard hasn't
// collected role since PR #28; the DB column was dropped in PR #35; the
// presets endpoint stopped serving the role-options array in this PR.
// No remaining consumers — type alias retired.
export type OnboardingUseCase =
  | 'multi_business'
  | 'single_business'
  | 'freelancer'
  | 'nonprofit'
  | 'personal_only';
export type OnboardingIndustry =
  | 'trades'
  | 'professional_services'
  | 'in_home_care'
  | 'nonprofit'
  | 'agency'
  | 'coaching'
  | 'real_estate'
  | 'healthcare'
  | 'education'
  | 'retail'
  | 'technology'
  | 'creative'
  | 'hospitality'
  | 'financial_services'
  | 'other';

export interface UserOnboardingState {
  id: string;
  isCompleted: boolean;
  step: OnboardingStep;
  // H-2C2 Phase 2 cleanup — `role` + `numberOfBusinesses` permanently
  // removed: UI dropped in PRs #28 / #29, DB columns dropped in PR #35,
  // and the `OnboardingRole` / `OnboardingRoleOption` type surface
  // retired in the presets-roles cleanup follow-up.
  primaryUseCase: OnboardingUseCase | null;
  industries: OnboardingIndustry[];
  createdAt: string;
  updatedAt: string;
}

export interface UpdateOnboardingStateRequest {
  step?: OnboardingStep;
  // H-2C2 Phase 2 cleanup — `role` + `numberOfBusinesses` no longer
  // accepted by PATCH /api/onboarding/state. Older clients that still
  // send those fields are tolerated server-side (fields ignored, no
  // 4xx) — but the frontend type no longer advertises them.
  primaryUseCase?: OnboardingUseCase | null;
  industries?: OnboardingIndustry[];
  isCompleted?: boolean;
}

export interface CompleteOnboardingResponse {
  success?: boolean;
  alreadyCompleted?: boolean;
  personalWorkspaceId: string | null;
  primaryBusinessWorkspaceId: string | null;
}

// H-2C2 Phase 2 cleanup follow-up — `OnboardingRoleOption` removed; the
// /api/onboarding/presets response no longer carries a `roles` array.

export interface OnboardingUseCaseOption {
  key: OnboardingUseCase;
  label: string;
  description: string;
}

export interface OnboardingIndustryOption {
  key: OnboardingIndustry;
  label: string;
  icon?: string;
}

export interface SuggestedModules {
  core: string[];
  optional: string[];
  description: string;
}

export interface OnboardingPresetsResponse {
  // H-2C2 Phase 2 cleanup follow-up — `roles` field removed; presets
  // endpoint no longer serves the role-options array.
  useCases: OnboardingUseCaseOption[];
  industries: OnboardingIndustryOption[];
  useCaseModules: Record<OnboardingUseCase, SuggestedModules>;
}

// ============================================================
// Error Tracking Types
// ============================================================

export type ErrorEventSource = 'backend' | 'frontend';
export type ErrorEventSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface FrontendErrorPayload {
  message: string;
  stack?: string;
  route?: string;
  context?: {
    browser?: string;
    userAgent?: string;
    viewport?: { width: number; height: number };
    extra?: Record<string, unknown>;
  };
}

export interface ErrorEventSummary {
  id: string;
  source: ErrorEventSource;
  severity: ErrorEventSeverity;
  message: string;
  route: string | null;
  method: string | null;
  statusCode: number | null;
  environment: string;
  workspaceId: string | null;
  userId: string | null;
  createdAt: string;
}

export interface ErrorEventDetail extends ErrorEventSummary {
  stack: string | null;
  context: Record<string, unknown> | null;
  userAgent: string | null;
  updatedAt: string;
}

export interface GetRecentErrorsResponse {
  errors: ErrorEventSummary[];
}

export interface GetErrorStatsResponse {
  totals: {
    backend: number;
    frontend: number;
    critical: number;
    error: number;
    warning: number;
  };
  last24Hours: {
    total: number;
    critical: number;
  };
}

// ============================================================
// AI Messaging Assistant Types
// ============================================================

export type ThreadSentiment = 'positive' | 'neutral' | 'negative' | 'mixed';
export type ThreadUrgency = 'low' | 'medium' | 'high';
export type SuggestionTone = 'professional' | 'friendly' | 'formal' | 'casual';
export type SuggestionCategory = 'acknowledge' | 'answer' | 'follow_up' | 'decline' | 'schedule' | 'clarify';
export type SuggestedActionType = 'create_task' | 'create_event' | 'add_contact' | 'send_follow_up' | 'add_memory' | 'update_contact';

export interface ThreadSummary {
  threadId: string;
  summary: string;
  keyPoints: string[];
  sentiment: ThreadSentiment;
  urgency: ThreadUrgency;
  lastUpdated: string;
}

export interface ReplySuggestion {
  id: string;
  text: string;
  tone: SuggestionTone;
  category: SuggestionCategory;
  confidence: number;
}

export interface SuggestedAction {
  id: string;
  type: SuggestedActionType;
  summary: string;
  explanation: string;
  payload: Record<string, unknown>;
  confidence: number;
}

export interface ThreadAssistantResponse {
  summary: ThreadSummary | null;
  suggestions: ReplySuggestion[];
  actions: SuggestedAction[];
}

export interface ContactCommunicationSummary {
  summary: string;
  recentActivity: string;
  openItems: string[];
  lastContactedAt: string | null;
  threadCount: number;
  unreadCount: number;
}

export interface AIMessagingStatusResponse {
  available: boolean;
  features: {
    threadSummary: boolean;
    replySuggestions: boolean;
    actionDetection: boolean;
    contactSummary: boolean;
  };
}

// ============================================================
// Thread Collaboration Types
// ============================================================

export type ParticipantRole = 'owner' | 'participant' | 'mentioned';

export interface ThreadParticipant {
  id: string;
  threadId: string;
  userId: string;
  workspaceId: string;
  role: ParticipantRole;
  addedByUserId: string | null;
  lastSeenAt: string | null;
  createdAt: string;
  updatedAt: string;
  user?: UserSummary;
  addedBy?: UserSummary;
}

export interface UserThreadPreference {
  id: string;
  threadId: string;
  userId: string;
  workspaceId: string;
  isMuted: boolean;
  isFollowing: boolean;
  mutedUntil: string | null;
  createdAt: string;
  updatedAt: string;
}

export type PinDuration = '1h' | '1d' | '1w' | 'forever';

export interface PinnedMessage {
  id: string;
  messageId: string;
  threadId: string;
  workspaceId: string;
  pinnedByUserId: string;
  duration: PinDuration;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  message?: CommunicationMessage;
  pinnedBy?: UserSummary;
}

// ============================================================
// Compliance domain
// ============================================================
// Backend contract:
//   server/src/routes/compliance.ts
//   server/src/models/Compliance*.ts
// Central Document Platform is NOT enabled today; uploads
// return HTTP 503 with code='central_documents_unavailable'.

export type ComplianceEnforcementMode =
  | 'informational'
  | 'warning'
  | 'approval_required'
  | 'hard_block';

export type ComplianceSubjectType =
  | 'contact'
  | 'provider_user'
  | 'provider_role'
  | 'department_circle'
  | 'central_team'
  | 'workspace';

export type ComplianceDocumentStatus =
  | 'pending_upload'
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'active'
  | 'expiring'
  | 'expired'
  | 'renewed'
  | 'archived'
  | 'blocked';

export type ComplianceApprovalStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'delegated'
  | 'cancelled';

export type ComplianceReviewOutcome = 'passed' | 'needs_attention' | 'replaced';

export type ComplianceReminderMilestone =
  | '90_days'
  | '60_days'
  | '30_days'
  | '14_days'
  | '7_days'
  | 'expired';

export type EligibilitySeverity = 'ok' | 'warning' | 'blocked';

export interface ComplianceCategory {
  id: string;
  workspaceId: string;
  key: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  isSystem: boolean;
  isActive: boolean;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface ComplianceRequirement {
  id: string;
  workspaceId: string;
  categoryId: string;
  name: string;
  description: string | null;
  requirementType: string | null;
  enforcementMode: ComplianceEnforcementMode;
  validityDays: number | null;
  renewalLeadDays: number | null;
  approvalRequired: boolean;
  reviewRequired: boolean;
  isMandatory: boolean;
  isActive: boolean;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  category?: Pick<ComplianceCategory, 'id' | 'key' | 'name' | 'icon' | 'color'> | null;
}

export interface ComplianceRequirementAssignment {
  id: string;
  workspaceId: string;
  requirementId: string;
  subjectType: ComplianceSubjectType;
  subjectId: string;
  effectiveFrom: string;
  effectiveUntil: string | null;
  isRequired: boolean;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  requirement?: ComplianceRequirement | null;
}

export interface ComplianceDocument {
  id: string;
  workspaceId: string;
  requirementId: string;
  requirementAssignmentId: string | null;
  subjectType: ComplianceSubjectType;
  subjectId: string;
  ownerUserId: string | null;
  title: string;
  status: ComplianceDocumentStatus;
  issuedAt: string | null;
  effectiveAt: string | null;
  expiresAt: string | null;
  reviewedAt: string | null;
  approvedAt: string | null;
  currentCentralDocumentId: string | null;
  temporaryFileAssetId: string | null;
  metadata: Record<string, unknown> | null;
  notes: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  /** Present on the create response only — flags whether Central
   *  Documents is currently reachable (today: always false). */
  centralDocumentsEnabled?: boolean;
  requirement?: ComplianceRequirement | null;
  requirementAssignment?: ComplianceRequirementAssignment | null;
  owner?: { id: string; fullName: string | null; email: string | null; avatarUrl: string | null } | null;
  approvals?: ComplianceApproval[];
  reviews?: ComplianceReview[];
}

export interface ComplianceApproval {
  id: string;
  workspaceId: string;
  complianceDocumentId: string;
  approverUserId: string | null;
  requestedByUserId: string | null;
  status: ComplianceApprovalStatus;
  reason: string | null;
  delegatedToUserId: string | null;
  requestedAt: string;
  decidedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface ComplianceReview {
  id: string;
  workspaceId: string;
  documentId: string;
  reviewerUserId: string | null;
  scheduledFor: string | null;
  completedAt: string | null;
  outcome: ComplianceReviewOutcome | null;
  notes: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface EligibilityRequirementLine {
  requirementId: string;
  requirementName: string;
  categoryId: string;
  enforcement: ComplianceEnforcementMode;
  assignmentId: string | null;
  coveredByDocumentId: string | null;
  coveredByStatus: string | null;
  expiresAt: string | null;
  satisfied: boolean;
  expiring: boolean;
  expired: boolean;
  severity: EligibilitySeverity;
  reason: string;
}

export interface EligibilityResult {
  status: EligibilitySeverity;
  requirements: EligibilityRequirementLine[];
  evaluatedAt: string;
}

export interface CoverageBucket {
  ok: number;
  warning: number;
  blocked: number;
  total: number;
}

export interface WorkspaceCoverageSummary {
  bySubjectType: {
    contact: CoverageBucket;
    provider_user: CoverageBucket;
    department_circle: CoverageBucket;
  };
  totals: CoverageBucket;
  evaluatedAt: string;
  hardBlockedRequirementIds: string[];
  expiringSoonCount: number;
  overdueExpiredCount: number;
}

export interface CoverageForSubjectRow {
  subjectId: string;
  displayName: string;
  status: EligibilitySeverity;
  missingCount: number;
  expiringCount: number;
  expiredCount: number;
}

export interface RequirementCoverageRow {
  requirementId: string;
  requirementName: string;
  enforcement: ComplianceEnforcementMode;
  categoryId: string;
  totalAssignments: number;
  satisfiedDocuments: number;
  expiringDocuments: number;
  expiredDocuments: number;
}

// Realtime payloads for COMPLIANCE_* events. The service always
// includes enough ids for the FE to pick the right RTK tags to
// invalidate; the full row is re-fetched via RTK Query.
export interface ComplianceRealtimePayload {
  requirementId?: string;
  assignmentId?: string;
  documentId?: string;
  approvalId?: string;
  predecessorId?: string;
  successorId?: string;
  subjectType?: ComplianceSubjectType;
  subjectId?: string;
  milestone?: ComplianceReminderMilestone;
}

export type CollaborationSignalType = 'typing' | 'on_call' | 'listening_voicemail' | 'idle';

export interface CollaborationSignal {
  threadId: string;
  userId: string;
  userName: string;
  userAvatar: string | null;
  signalType: CollaborationSignalType;
  metadata?: {
    callId?: string;
    voicemailId?: string;
    messageId?: string;
  };
  timestamp: string;
}
